import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { DealershipTask, Employee, TaskParticipant, TaskRemark, TaskType, RecurrenceFrequency, TaskExtensionStatus, ProofPhoto, TaskProof, TaskComment, MessageReadReceipt, MessageReaction, TaskCommentMediaType } from '../types';
import {
  ArrowLeft, User, Calendar, Clock, Check, Camera,
  Edit, Trash2, UserPlus, Play, RotateCcw,
  Send, X, ChevronDown, CalendarClock, Layers, CheckCircle, Phone,
  Eye, GitFork, Flag, Plus, Loader2, Paperclip, Reply, FileText,
  ImageIcon, Video, Music4, CheckCheck, ThumbsUp
} from 'lucide-react';
import { supabase } from '../src/lib/supabase';
import LoadingButton from '../src/components/ui/LoadingButton';
import TwelveHourTimePicker, { getRoundedFiveMinuteTime } from '../src/components/ui/TwelveHourTimePicker';
import { getTaskProofPhotos, MAX_TASK_PROOF_PHOTOS } from '../src/utils/taskProofs';
import { logSupabaseQueryError } from '../src/utils/errorLogging';
import { resolveCurrentEmployeeIdForApproval, resolveEmployeeIdForApproval } from '../src/utils/approvalEmployeeIds';
import { toast } from 'sonner';
import MessageReactionChip from './MessageReactionChip';

interface TaskDetailsScreenProps {
  task: DealershipTask;
  subTasks: DealershipTask[];
  parentTask?: DealershipTask;
  employees: Employee[];
  currentUser: Employee;
  readOnly?: boolean;
  onBack: () => void;
  onStartTask: () => Promise<void> | void;
  onReopenTask: () => Promise<void> | void;
  onCompleteTask: (proof: TaskProof) => Promise<void> | void;
  onSaveTaskProof?: (proof: TaskProof) => Promise<void> | void;
  onCompleteTaskWithoutPhoto: () => Promise<void> | void;
  onReassign: () => void;
  onDelegate: () => void;
  onDelete: () => Promise<void> | void;
  chatScrollSignal?: number;
  onInlineEditSave?: (
    taskId: string,
    payload: {
      description: string;
      assignedTo: string | null;
      deadline?: number;
      requirePhoto: boolean;
      taskType: TaskType;
      recurrenceFrequency: RecurrenceFrequency | null;
      recurrenceTime?: string | null;
      priority: 'High' | 'Medium';
    }
  ) => Promise<boolean>;
  onAddRemark?: (
    taskId: string,
    remark:
      | string
      | {
          text: string;
          mentionedUserIds?: string[];
          mentionedDisplayNames?: string[];
        }
  ) => Promise<void> | void;
}

// --- Helpers ---

interface NormalizedTaskRemark extends TaskRemark {
  timestamp: number;
  employeeName: string;
}

interface TaskRemarkGroup {
  dateKey: string;
  label: string;
  remarks: NormalizedTaskRemark[];
}

interface MentionCandidate {
  id: string;
  name: string;
}

interface ParentTaskSummary {
  id: string;
  description: string;
  title?: string | null;
}

interface ProofDraft {
  id: string;
  file: File;
  previewUrl: string;
}

interface TaskCommentQuote {
  id: string;
  sender_name: string;
  message_text: string;
  media_type: TaskCommentMediaType | null;
  media_name: string | null;
}

interface TaskCommentView extends TaskComment {
  sender_name: string;
  sender_initials: string;
  created_at_ms: number;
  reply_to?: TaskCommentQuote | null;
  optimistic?: boolean;
  upload_progress?: number;
}

interface PendingChatMedia {
  id: string;
  file: File;
  mediaType: TaskCommentMediaType;
  name: string;
  size: number;
  previewUrl: string | null;
}

type MessageActionMenuState = {
  messageId: string;
  isOwn: boolean;
  canEdit: boolean;
  x: number;
  y: number;
} | null;

type MediaPreviewState = {
  url: string;
  type: TaskCommentMediaType;
  name: string;
} | null;

type ReactionTooltipState = {
  messageId: string;
  left: number;
  top: number;
} | null;

type AttachmentMenuPosition = {
  left: number;
  bottom: number;
  width: number;
} | null;

const SEND_MENTION_PUSH_URL = 'https://xdvybqfivmzfddmszqqk.supabase.co/functions/v1/send-mention-push';
const CHAT_MEDIA_BUCKET = 'chat-media';
const MAX_CHAT_MEDIA_BYTES = 50 * 1024 * 1024;
const TASK_CHAT_PAGE_SIZE = 30;
const TASK_COMMENT_COLUMNS = 'id, task_id, sender_id, message_text, created_at, reply_to_id, media_url, media_type, media_name, media_size, is_edited, edited_at';

const logTaskChatPerf = (label: string, payload?: Record<string, unknown>) => {
  if (typeof window === 'undefined' || window.localStorage.getItem('opentask:chat-debug') !== '1') return;
  console.debug(`[task-chat] ${label}`, payload || {});
};

const sanitizeChatFileName = (value: string): string =>
  String(value || 'file').replace(/[^a-zA-Z0-9._-]/g, '_');

const isMessageWithinEditWindow = (createdAt: string): boolean =>
  Date.now() - new Date(createdAt).getTime() < 60 * 60 * 1000;

const getEmployeeInitials = (value: string): string => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
};

const formatFileSize = (bytes: number | null | undefined): string => {
  const safeBytes = Number(bytes || 0);
  if (!Number.isFinite(safeBytes) || safeBytes <= 0) return '0 B';
  if (safeBytes < 1024) return `${safeBytes} B`;
  if (safeBytes < 1024 * 1024) return `${(safeBytes / 1024).toFixed(1)} KB`;
  return `${(safeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getChatDateLabel = (timestamp: number): string => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const matchesDay = (left: Date, right: Date) =>
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate();

  if (matchesDay(date, today)) return 'Today';
  if (matchesDay(date, yesterday)) return 'Yesterday';

  return date.toLocaleDateString([], {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === today.getFullYear() ? undefined : 'numeric',
  });
};

const getMediaTypeFromFile = (file: File): TaskCommentMediaType => {
  const mimeType = String(file.type || '').toLowerCase();
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  return 'file';
};

const getQuotedMessageText = (message: {
  message_text?: string | null;
  media_type?: TaskCommentMediaType | null;
  media_name?: string | null;
}): string => {
  const text = String(message.message_text || '').trim();
  if (text) return text.length > 72 ? `${text.slice(0, 72)}…` : text;
  if (message.media_type === 'image') return 'Photo';
  if (message.media_type === 'video') return 'Video';
  if (message.media_type === 'audio') return 'Audio';
  if (message.media_name) return message.media_name;
  return 'Attachment';
};

const isBlobUrl = (value: string | null | undefined): boolean =>
  String(value || '').startsWith('blob:');

const areTaskMessagesEqual = (left: TaskCommentView, right: TaskCommentView): boolean =>
  left.id === right.id &&
  left.task_id === right.task_id &&
  left.sender_id === right.sender_id &&
  left.message_text === right.message_text &&
  left.created_at === right.created_at &&
  left.reply_to_id === right.reply_to_id &&
  left.media_url === right.media_url &&
  left.media_type === right.media_type &&
  left.media_name === right.media_name &&
  left.media_size === right.media_size &&
  Boolean(left.is_edited) === Boolean(right.is_edited) &&
  String(left.edited_at || '') === String(right.edited_at || '') &&
  Boolean(left.optimistic) === Boolean(right.optimistic) &&
  left.upload_progress === right.upload_progress;

const isLikelyOptimisticEcho = (optimistic: TaskCommentView, incoming: TaskCommentView): boolean =>
  Boolean(optimistic.optimistic) &&
  !incoming.optimistic &&
  optimistic.task_id === incoming.task_id &&
  optimistic.sender_id === incoming.sender_id &&
  optimistic.message_text === incoming.message_text &&
  String(optimistic.reply_to_id || '') === String(incoming.reply_to_id || '') &&
  Math.abs(optimistic.created_at_ms - incoming.created_at_ms) < 120000;

const mergeTaskMessages = (
  current: TaskCommentView[],
  incoming: TaskCommentView[],
  mode: 'append' | 'prepend' | 'replace' = 'append'
): TaskCommentView[] => {
  if (mode === 'replace') {
    return incoming;
  }

  const nextById = new Map(current.map((message) => [message.id, message]));
  let changed = false;

  incoming.forEach((message) => {
    const optimisticEcho = current.find((existing) => isLikelyOptimisticEcho(existing, message));
    if (optimisticEcho) {
      nextById.delete(optimisticEcho.id);
      if (isBlobUrl(optimisticEcho.media_url) && optimisticEcho.media_url !== message.media_url) {
        URL.revokeObjectURL(String(optimisticEcho.media_url));
      }
      changed = true;
    }

    const existing = nextById.get(message.id);
    if (!existing) {
      nextById.set(message.id, message);
      changed = true;
      return;
    }

    const merged = {
      ...existing,
      ...message,
      reply_to: message.reply_to || existing.reply_to || null,
      optimistic: message.optimistic ?? existing.optimistic,
      upload_progress: message.upload_progress ?? existing.upload_progress,
    };
    if (!areTaskMessagesEqual(existing, merged) || existing.reply_to !== merged.reply_to) {
      nextById.set(message.id, merged);
      changed = true;
    }
  });

  if (!changed) {
    return current;
  }

  return Array.from(nextById.values()).sort((left, right) => left.created_at_ms - right.created_at_ms);
};

const removeTaskMessage = (current: TaskCommentView[], messageId: string): TaskCommentView[] => {
  const target = current.find((message) => message.id === messageId);
  if (!target) return current;
  if (isBlobUrl(target.media_url)) {
    URL.revokeObjectURL(String(target.media_url));
  }
  return current.filter((message) => message.id !== messageId);
};

const compressImageFile = async (file: File): Promise<File> => {
  if (!file.type.startsWith('image/') || file.type === 'image/gif' || file.size < 450 * 1024) {
    return file;
  }
  if (typeof createImageBitmap !== 'function') {
    return file;
  }

  const bitmap = await createImageBitmap(file);
  const maxEdge = 1600;
  const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    return file;
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.78);
  });

  if (!blob || blob.size >= file.size) {
    return file;
  }

  const compressedName = file.name.replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${compressedName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

const formatFullDate = (timestamp: number | undefined | null): string => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const formatRemarkDateHeader = (timestamp: number): string => {
  const d = new Date(timestamp);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getDate()).padStart(2, '0')} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const formatRemarkTime = (timestamp: number): string =>
  new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });

const getRemarkDateKey = (timestamp: number): string => {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
};

const parseTimestamp = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
    const p = Date.parse(value);
    if (Number.isFinite(p)) return p;
  }
  return null;
};

const escapeRegex = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const extractMentionContext = (text: string, caretPosition: number): { start: number; query: string } | null => {
  const safeCaret = Math.max(0, Math.min(caretPosition, text.length));
  const textBeforeCaret = text.slice(0, safeCaret);
  const atIndex = textBeforeCaret.lastIndexOf('@');
  if (atIndex < 0) return null;

  const beforeAt = textBeforeCaret.slice(0, atIndex);
  const charBeforeAt = beforeAt.slice(-1);
  if (charBeforeAt && !/\s|[([{]/.test(charBeforeAt)) return null;

  const mentionQuery = textBeforeCaret.slice(atIndex + 1);
  if (!/^[\w.\- ]{0,40}$/.test(mentionQuery)) return null;
  if (mentionQuery.includes('\n')) return null;

  return { start: atIndex, query: mentionQuery.trimStart() };
};

const formatDateTimeForInput = (timestamp?: number | null): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const offset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(timestamp - offset).toISOString().slice(0, 16);
};

const formatDateTimeWithMeridiem = (timestamp: number | undefined | null): string => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const hours24 = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours24 >= 12 ? 'PM' : 'AM';
  const hours12 = hours24 % 12 || 12;
  return `${day} ${month}, ${hours12}:${minutes} ${ampm}`;
};

const formatDateTimeLabel = (value: string): string => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.replace('T', ' ');
  }
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const toIsoStringFromMs = (timestamp: number): string | null => {
  if (!Number.isFinite(timestamp)) return null;
  const isoString = new Date(timestamp).toISOString();
  return Number.isNaN(Date.parse(isoString)) ? null : isoString;
};

const getTaskCreatorId = (taskRow: Record<string, unknown> | null | undefined): string => {
  return String(
    taskRow?.createdBy ??
    taskRow?.created_by ??
    taskRow?.assignedBy ??
    taskRow?.assigned_by ??
    ''
  ).trim();
};

const normalizeRecurrenceTime = (value: string | null | undefined): string | null => {
  const text = String(value || '').trim();
  if (!/^\d{2}:\d{2}$/.test(text)) return null;
  const [hh, mm] = text.split(':').map(Number);
  if (!Number.isInteger(hh) || !Number.isInteger(mm)) return null;
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

const formatRecurrenceTimeLabel = (value: string | null): string => {
  if (!value) return 'Not set';
  const date = new Date(`1970-01-01T${value}:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const getRoleBadge = (role?: string): { label: string; className: string } => {
  if (role === 'owner' || role === 'super_admin') return { label: 'OWNER', className: 'bg-[var(--accent-light)] text-[var(--accent)]' };
  if (role === 'manager') return { label: 'MANAGER', className: 'bg-slate-200 text-slate-700' };
  return { label: 'FIELD STAFF', className: 'bg-amber-100 text-amber-700' };
};

// --- Component ---

const TaskDetailsScreen: React.FC<TaskDetailsScreenProps> = ({
  task, subTasks, parentTask, employees, currentUser, onBack,
  onStartTask, onReopenTask, onCompleteTask, onSaveTaskProof, onCompleteTaskWithoutPhoto,
  onReassign, onDelegate, onDelete, chatScrollSignal = 0, onInlineEditSave, onAddRemark, readOnly = false
}) => {
  const [activeTab, setActiveTab] = useState<'chat' | 'details'>('chat');
  const [isTaskKeyboardActive, setIsTaskKeyboardActive] = useState(false);
  const [newRemark, setNewRemark] = useState('');
  const [remarkAddedThisSession, setRemarkAddedThisSession] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedProofIndex, setSelectedProofIndex] = useState<number | null>(null);
  const [showExtensionInput, setShowExtensionInput] = useState(false);
  const [extensionDate, setExtensionDate] = useState('');
  const [isExtensionUpdating, setIsExtensionUpdating] = useState(false);
  const [extensionApproverId, setExtensionApproverId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isReopening, setIsReopening] = useState(false);
  const [isStoppingRecurrence, setIsStoppingRecurrence] = useState(false);
  const [editDescription, setEditDescription] = useState(task.description);
  const [editAssigneeId, setEditAssigneeId] = useState(task.assignedTo || 'none');
  const [editDeadline, setEditDeadline] = useState('');
  const [editRequirePhoto, setEditRequirePhoto] = useState(Boolean(task.requirePhoto));
  const [editIsHighPriority, setEditIsHighPriority] = useState(false);
  const [editTaskType, setEditTaskType] = useState<TaskType>('one_time');
  const [editRecurrenceFrequency, setEditRecurrenceFrequency] = useState<RecurrenceFrequency | ''>('');
  const [editRecurrenceTime, setEditRecurrenceTime] = useState<string>(getRoundedFiveMinuteTime());
  const [editModalKeyboardInset, setEditModalKeyboardInset] = useState(0);
  const [editModalVisibleHeight, setEditModalVisibleHeight] = useState<number | null>(null);
  const [mentionCandidates, setMentionCandidates] = useState<MentionCandidate[]>([]);
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
  const [mentionLoading, setMentionLoading] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [selectedMentions, setSelectedMentions] = useState<MentionCandidate[]>([]);
  const [messages, setMessages] = useState<TaskCommentView[]>([]);
  const [messageReceiptsById, setMessageReceiptsById] = useState<Record<string, MessageReadReceipt[]>>({});
  const [reactionsByMessageId, setReactionsByMessageId] = useState<Record<string, MessageReaction[]>>({});
  const [reactionNameOverridesByUserId, setReactionNameOverridesByUserId] = useState<Record<string, string>>({});
  const [reactionViewerMessageId, setReactionViewerMessageId] = useState<string | null>(null);
  const [reactionTooltip, setReactionTooltip] = useState<ReactionTooltipState>(null);
  const [loadingMessages, setLoadingMessages] = useState(true);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [hasOlderMessages, setHasOlderMessages] = useState(false);
  const [selectedChatMedia, setSelectedChatMedia] = useState<PendingChatMedia | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [replyingToMessage, setReplyingToMessage] = useState<TaskCommentView | null>(null);
  const [editingMessage, setEditingMessage] = useState<TaskCommentView | null>(null);
  const [messageActionMenu, setMessageActionMenu] = useState<MessageActionMenuState>(null);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [mediaPreview, setMediaPreview] = useState<MediaPreviewState>(null);
  const [attachmentMenuPosition, setAttachmentMenuPosition] = useState<AttachmentMenuPosition>(null);
  const [proofDrafts, setProofDrafts] = useState<ProofDraft[]>([]);
  const [uploadingProofDraftIds, setUploadingProofDraftIds] = useState<string[]>([]);
  const [resolvedParentTask, setResolvedParentTask] = useState<ParentTaskSummary | null>(() =>
    parentTask
      ? {
          id: String(parentTask.id || ''),
          description: String(parentTask.description || ''),
          title: null,
        }
      : null
  );
  const remarksScrollRef = useRef<HTMLDivElement | null>(null);
  const remarkInputRef = useRef<HTMLTextAreaElement | null>(null);
  const editModalScrollRef = useRef<HTMLDivElement | null>(null);
  const extensionInputRef = useRef<HTMLInputElement | null>(null);
  const proofInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const photoLibraryInputRef = useRef<HTMLInputElement | null>(null);
  const documentInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentButtonRef = useRef<HTMLButtonElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const proofDraftsRef = useRef<ProofDraft[]>([]);
  const messageRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const messageIdsRef = useRef<string[]>([]);
  const messagesRef = useRef<TaskCommentView[]>([]);
  const messageReceiptsByIdRef = useRef<Record<string, MessageReadReceipt[]>>({});
  const selectedChatMediaRef = useRef<PendingChatMedia | null>(null);
  const taskChatChannelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const taskChatLoadSeqRef = useRef(0);
  const taskChatEventCountRef = useRef(0);
  const loadTaskMessagesRef = useRef<((options?: { silent?: boolean }) => Promise<void>) | null>(null);
  const backfillTaskMessagesRef = useRef<(() => Promise<void>) | null>(null);
  const normalizeRealtimeMessageRowRef = useRef<((row: Partial<TaskComment> | null | undefined) => TaskCommentView | null) | null>(null);
  const markMessagesAsReadRef = useRef<((messagesToMark: TaskCommentView[]) => Promise<void>) | null>(null);
  const isCurrentUserSenderIdRef = useRef<((senderId?: string | null) => boolean) | null>(null);
  const hasAnchoredInitialMessagesRef = useRef(false);
  const previousMessageLengthRef = useRef(0);
  const isNearChatBottomRef = useRef(true);
  const pendingOwnMessageScrollRef = useRef(false);
  const renderCountRef = useRef(0);
  const longPressTimeoutRef = useRef<number | null>(null);
  const touchGestureRef = useRef<{ startX: number; startY: number; replyTriggered: boolean } | null>(null);

  const syncInputOnPickerClose = (input: HTMLInputElement | null, setter: (value: string) => void) => {
    if (!input) return;
    const tick = () => {
      if (document.activeElement === input) {
        requestAnimationFrame(tick);
      } else {
        setter(input.value);
      }
    };
    requestAnimationFrame(tick);
  };

  const openExtensionPicker = () => {
    const input = extensionInputRef.current;
    if (!input) return;
    if (typeof (input as HTMLInputElement & { showPicker?: () => void }).showPicker === 'function') {
      (input as HTMLInputElement & { showPicker: () => void }).showPicker();
      return;
    }
    input.focus();
    input.click();
  };

  const withTimeout = useMemo(
    () =>
      <T,>(promise: Promise<T>, ms: number): Promise<T> =>
        Promise.race([
          promise,
          new Promise<T>((_, reject) =>
            setTimeout(() => reject(new Error('Operation timed out')), ms)
          ),
        ]),
    []
  );

  const updateAttachmentMenuPosition = useCallback(() => {
    if (typeof window === 'undefined') return;
    const button = attachmentButtonRef.current;
    if (!button) return;

    const rect = button.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportWidth = viewport?.width || window.innerWidth;
    const viewportHeight = viewport?.height || window.innerHeight;
    const viewportOffsetLeft = viewport?.offsetLeft || 0;
    const viewportOffsetTop = viewport?.offsetTop || 0;
    const menuWidth = Math.min(320, Math.max(256, viewportWidth - 32));
    const viewportLeft = viewportOffsetLeft;
    const viewportRight = viewportOffsetLeft + viewportWidth;
    const viewportBottom = viewportOffsetTop + viewportHeight;
    const left = Math.max(
      viewportLeft + 16,
      Math.min(rect.left + viewportOffsetLeft, viewportRight - menuWidth - 16)
    );
    const bottom = Math.max(16, window.innerHeight - Math.min(rect.top + viewportOffsetTop, viewportBottom) + 12);

    setAttachmentMenuPosition({ left, bottom, width: menuWidth });
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const container = remarksScrollRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom <= 100;
    isNearChatBottomRef.current = isNearBottom;
    setShowScrollBtn(distanceFromBottom > 100);
  }, []);

  const scrollTaskMessagesToBottom = useCallback((behavior: ScrollBehavior | 'instant' = 'auto') => {
    const container = remarksScrollRef.current;
    if (!container) {
      return;
    }

    const scrollTop = Math.max(0, container.scrollHeight - container.clientHeight);

    if (behavior === 'smooth') {
      container.scrollTo({ top: scrollTop, behavior: 'smooth' });
      return;
    }

    container.scrollTop = scrollTop;
    isNearChatBottomRef.current = true;
    setShowScrollBtn(false);
  }, []);

  useEffect(() => {
    const parentTaskId = String(task.parentTaskId || '').trim();
    if (!parentTaskId) {
      setResolvedParentTask(null);
      return;
    }

    if (parentTask && String(parentTask.id || '').trim() === parentTaskId) {
      setResolvedParentTask({
        id: parentTaskId,
        description: String(parentTask.description || ''),
        title: null,
      });
      return;
    }

    let cancelled = false;

    const loadParentTask = async () => {
      const { data, error } = await supabase
        .from('tasks')
        .select('id, description, title')
        .eq('id', parentTaskId)
        .single();

      if (cancelled) return;

      if (error) {
        console.warn('Failed to load parent task details:', error);
        logSupabaseQueryError(error);
        setResolvedParentTask(null);
        return;
      }

      setResolvedParentTask({
        id: String((data as any)?.id || parentTaskId),
        description: String((data as any)?.description || ''),
        title: typeof (data as any)?.title === 'string' ? (data as any).title : null,
      });
    };

    void loadParentTask();

    return () => {
      cancelled = true;
    };
  }, [task.parentTaskId, parentTask?.id, parentTask?.description]);

  // --- Derived values ---
  const getEmployeeName = (id?: string | null): string => {
    if (!id) return 'Unassigned';
    return employees.find(e => e.id === id)?.name || 'Unknown';
  };

  const getEmployeeRole = (id?: string | null): string | undefined => {
    if (!id) return undefined;
    return employees.find(e => e.id === id)?.role;
  };

  const getEmployeeMobile = (id?: string | null): string => {
    if (!id) return '';
    return String(employees.find(e => e.id === id)?.mobile || '');
  };

  const getTelHref = (mobile: string): string | null => {
    const trimmed = String(mobile || '').trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('+')) {
      const digits = trimmed.slice(1).replace(/\D/g, '');
      return digits ? `tel:+${digits}` : null;
    }
    const digits = trimmed.replace(/\D/g, '');
    return digits ? `tel:${digits}` : null;
  };

  const isOverdue =
    task.status !== 'completed' &&
    task.status !== 'pending_approval' &&
    task.deadline != null &&
    Date.now() > task.deadline;
  const assigneeName = getEmployeeName(task.assignedTo);
  const assignerName = getEmployeeName(task.assignedBy);
  const rawPriority = String((task as any).priority || '').trim().toLowerCase();
  const normalizedPriority = rawPriority === 'high' ? 'High' : rawPriority === 'low' ? 'Low' : 'Medium';
  const isPendingApproval = task.status === 'pending_approval';
  const overdueDays = isOverdue && task.deadline ? Math.max(1, Math.ceil((Date.now() - task.deadline) / 86400000)) : 0;
  const currentEmployeeId = String(currentUser.auth_user_id || currentUser.id || '').trim();
  const progressValue =
    task.status === 'completed'
      ? 100
      : task.status === 'pending_approval'
      ? 82
      : task.status === 'in-progress'
      ? 62
      : 24;
  const assigneeTelHref = getTelHref(getEmployeeMobile(task.assignedTo));
  const assignerTelHref = getTelHref(getEmployeeMobile(task.assignedBy));

  const isManager = currentUser.role === 'manager' || currentUser.role === 'super_admin' || currentUser.role === 'owner';
  renderCountRef.current += 1;
  useEffect(() => {
    logTaskChatPerf('render', {
      taskId: task.id,
      count: renderCountRef.current,
      messages: messagesRef.current.length,
    });
  });

  const mentionableMembers = useMemo<MentionCandidate[]>(
    () =>
      (employees || [])
        .filter((employee) => employee?.id && employee.id !== currentUser.id)
        .map((employee) => ({
          id: employee.id,
          name: String(employee.name || '').trim() || 'User',
        })),
    [employees, currentUser.id]
  );

  const rawTaskType = String((task as any).taskType ?? (task as any).task_type ?? '').toLowerCase();
  const rawRecurrence = String((task as any).recurrenceFrequency ?? (task as any).recurrence_frequency ?? '').toLowerCase();
  const recurrenceTime = normalizeRecurrenceTime((task as any).recurrenceTime ?? (task as any).recurrence_time);
  const normalizedTaskType: TaskType = rawTaskType === 'recurring' ? 'recurring' : 'one_time';
  const normalizedRecurrence = (rawRecurrence === 'daily' || rawRecurrence === 'weekly' || rawRecurrence === 'monthly') ? rawRecurrence : null;

  const rawExtensionStatus = String((task as any).extensionStatus ?? (task as any).extension_status ?? 'NONE').toUpperCase();
  const extensionStatus: TaskExtensionStatus =
    rawExtensionStatus === 'REQUESTED' || rawExtensionStatus === 'APPROVED' || rawExtensionStatus === 'REJECTED'
      ? (rawExtensionStatus as TaskExtensionStatus) : 'NONE';
  const requestedDueDate = parseTimestamp((task as any).requestedDueDate) ?? parseTimestamp((task as any).requested_due_date);

  const assignedEmployee = task.assignedTo ? employees.find(e => e.id === task.assignedTo) : undefined;
  const assignerEmployee = task.assignedBy ? employees.find(e => e.id === task.assignedBy) : undefined;
  const normalizedCurrentEmail = String(currentUser.email || '').trim().toLowerCase();
  const normalizedAssignedEmail = String(assignedEmployee?.email || '').trim().toLowerCase();
  const normalizedAssignerEmail = String(assignerEmployee?.email || '').trim().toLowerCase();

  const isAssignedWorker =
    task.assignedTo === currentUser.id ||
    (typeof currentUser.auth_user_id === 'string' && task.assignedTo === currentUser.auth_user_id) ||
    (typeof assignedEmployee?.auth_user_id === 'string' && assignedEmployee.auth_user_id === currentUser.id) ||
    (typeof assignedEmployee?.auth_user_id === 'string' && typeof currentUser.auth_user_id === 'string' && assignedEmployee.auth_user_id === currentUser.auth_user_id) ||
    Boolean(normalizedCurrentEmail && normalizedAssignedEmail && normalizedCurrentEmail === normalizedAssignedEmail);

  const isTaskAssigner =
    task.assignedBy === currentUser.id ||
    (typeof currentUser.auth_user_id === 'string' && task.assignedBy === currentUser.auth_user_id) ||
    (typeof assignerEmployee?.auth_user_id === 'string' && assignerEmployee.auth_user_id === currentUser.id) ||
    (typeof assignerEmployee?.auth_user_id === 'string' && typeof currentUser.auth_user_id === 'string' && assignerEmployee.auth_user_id === currentUser.auth_user_id) ||
    Boolean(normalizedCurrentEmail && normalizedAssignerEmail && normalizedCurrentEmail === normalizedAssignerEmail);

  const collaboratorIds = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...(Array.isArray(task.participantUserIds) ? task.participantUserIds : []),
            ...(Array.isArray(task.participants)
              ? task.participants.map((participant: Partial<TaskParticipant>) => String(participant?.user_id || '').trim())
              : []),
          ]
            .map((id) => String(id || '').trim())
            .filter(
              (id) =>
                Boolean(id) &&
                id !== String(task.assignedTo || '').trim() &&
                id !== String(task.assignedBy || '').trim()
            )
        )
      ),
    [task.assignedBy, task.assignedTo, task.participantUserIds, task.participants]
  );

  const collaboratorSummaries = useMemo(
    () =>
      collaboratorIds.map((id) => ({
        id,
        name: getEmployeeName(id),
        telHref: getTelHref(getEmployeeMobile(id)),
      })),
    [collaboratorIds, employees]
  );

  const isCurrentUserSenderId = useCallback((senderId?: string | null): boolean => {
    const normalizedSenderId = String(senderId || '').trim();
    if (!normalizedSenderId) return false;
    return (
      normalizedSenderId === String(currentUser.id || '').trim() ||
      (typeof currentUser.auth_user_id === 'string' &&
        normalizedSenderId === String(currentUser.auth_user_id || '').trim())
    );
  }, [currentUser.auth_user_id, currentUser.id]);

  useEffect(() => {
    isCurrentUserSenderIdRef.current = isCurrentUserSenderId;
  }, [isCurrentUserSenderId]);

  const senderNameById = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    employees.forEach((employee) => {
      const employeeId = String(employee.id || '').trim();
      const authUserId = String(employee.auth_user_id || '').trim();
      const name = String(employee.name || '').trim() || 'User';
      if (employeeId) {
        map[employeeId] = name;
      }
      if (authUserId && !map[authUserId]) {
        map[authUserId] = name;
      }
    });
    if (currentUser.id) {
      map[String(currentUser.id)] = String(currentUser.name || '').trim() || 'You';
    }
    if (currentUser.auth_user_id) {
      map[String(currentUser.auth_user_id)] = String(currentUser.name || '').trim() || 'You';
    }
    return map;
  }, [currentUser.auth_user_id, currentUser.id, currentUser.name, employees]);

  const getReactionDisplayNames = useCallback((messageId: string): string[] => {
    const seen = new Set<string>();
    return (reactionsByMessageId[messageId] || [])
      .map((reaction) => {
        const userId = String(reaction.user_id || '').trim();
        if (!userId || seen.has(userId)) {
          return '';
        }
        seen.add(userId);
        return reactionNameOverridesByUserId[userId] || senderNameById[userId] || 'Unknown User';
      })
      .filter(Boolean);
  }, [reactionNameOverridesByUserId, reactionsByMessageId, senderNameById]);

  const fetchReactionNames = useCallback(async (messageId: string) => {
    const normalizedMessageId = String(messageId || '').trim();
    if (!normalizedMessageId) return;

    const { data: reactionRows, error: reactionError } = await supabase
      .from('message_reactions')
      .select('user_id')
      .eq('message_id', normalizedMessageId);

    if (reactionError) {
      console.warn('Failed to load reaction users:', reactionError);
      logSupabaseQueryError(reactionError);
      return;
    }

    const unknownUserIds = Array.from(
      new Set(
        (reactionRows || [])
          .map((row: any) => String(row?.user_id || '').trim())
          .filter((userId) =>
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userId) &&
            !senderNameById[userId] &&
            !reactionNameOverridesByUserId[userId]
          )
      )
    );

    if (unknownUserIds.length === 0) {
      return;
    }

    let employeeQuery = supabase
      .from('employees')
      .select('name, auth_user_id')
      .in('auth_user_id', unknownUserIds);

    const taskCompanyId = String(task.company_id || currentUser.company_id || '').trim();
    if (taskCompanyId) {
      employeeQuery = employeeQuery.eq('company_id', taskCompanyId);
    }

    const { data: employeeRows, error: employeeError } = await employeeQuery;

    if (employeeError) {
      console.warn('Failed to resolve reaction names:', employeeError);
      logSupabaseQueryError(employeeError);
      return;
    }

    const nextNames: Record<string, string> = {};
    (employeeRows || []).forEach((employee: any) => {
      const authUserId = String(employee?.auth_user_id || '').trim();
      const name = String(employee?.name || '').trim();
      if (authUserId && name) {
        nextNames[authUserId] = name;
      }
    });

    if (Object.keys(nextNames).length > 0) {
      setReactionNameOverridesByUserId((prev) => ({ ...prev, ...nextNames }));
    }
  }, [
    currentUser.company_id,
    reactionNameOverridesByUserId,
    senderNameById,
    task.company_id,
  ]);

  // Only the task assigner can edit or delete the task
  const canDelete = isTaskAssigner;
  const canEdit = isTaskAssigner;
  const canDelegateTask = isManager || isAssignedWorker || isTaskAssigner;

  const canApproveExtension = Boolean(extensionApproverId) && currentUser.id === extensionApproverId;
  const canRequestExtension = isAssignedWorker && task.status === 'in-progress' && extensionStatus !== 'REQUESTED';
  const canReviewExtensionRequest = canApproveExtension && !isAssignedWorker && task.status === 'in-progress' && extensionStatus === 'REQUESTED' && Boolean(requestedDueDate);

  // --- Remarks ---
  const normalizedRemarks = useMemo(() => {
    const raw = Array.isArray(task.remarks) ? task.remarks : [];
    return raw
      .map((remark, index): NormalizedTaskRemark | null => {
        if (!remark || typeof remark !== 'object') return null;
        const rawTs = (remark as { timestamp?: number | string }).timestamp;
        const numTs = typeof rawTs === 'number' ? rawTs : typeof rawTs === 'string' && rawTs.trim() ? (Number.isFinite(Number(rawTs)) ? Number(rawTs) : Date.parse(rawTs)) : NaN;
        const resolvedTs = Number.isFinite(numTs) ? numTs : Date.now();
        const fallbackName = (remark.employeeId && getEmployeeName(remark.employeeId)) || 'Unknown User';
        const resolvedName = typeof remark.employeeName === 'string' && remark.employeeName.trim() ? remark.employeeName.trim() : fallbackName;
        const remarkText = typeof remark.remark === 'string' ? remark.remark.trim() : '';
        if (!remarkText) return null;
        return { ...remark, id: remark.id || `remark_${task.id}_${index}`, employeeName: resolvedName, timestamp: resolvedTs, remark: remarkText };
      })
      .filter((r): r is NormalizedTaskRemark => Boolean(r))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [task.remarks, task.id, employees]);

  const groupedRemarks = useMemo<TaskRemarkGroup[]>(() => {
    const groups: TaskRemarkGroup[] = [];
    const map = new Map<string, TaskRemarkGroup>();
    normalizedRemarks.forEach(remark => {
      const key = getRemarkDateKey(remark.timestamp);
      const existing = map.get(key);
      if (existing) { existing.remarks.push(remark); return; }
      const group: TaskRemarkGroup = { dateKey: key, label: formatRemarkDateHeader(remark.timestamp), remarks: [remark] };
      map.set(key, group);
      groups.push(group);
    });
    return groups;
  }, [normalizedRemarks]);

  const normalizeTaskComments = useCallback((rows: TaskComment[]): TaskCommentView[] => {
    const normalizedRows = (rows || [])
      .map((row) => {
        const senderId = String(row.sender_id || '').trim();
        const senderName =
          senderNameById[senderId] ||
          (isCurrentUserSenderId(senderId)
            ? String(currentUser.name || '').trim() || 'You'
            : 'Unknown');

        return {
          ...row,
          id: String(row.id || `temp-${Date.now()}`),
          task_id: String(row.task_id || task.id),
          sender_id: senderId,
          message_text: String(row.message_text || ''),
          created_at: String(row.created_at || new Date().toISOString()),
          created_at_ms: parseTimestamp(row.created_at) || Date.now(),
          reply_to_id: row.reply_to_id ? String(row.reply_to_id) : null,
          media_url: row.media_url ? String(row.media_url) : null,
          media_type: row.media_type || null,
          media_name: row.media_name ? String(row.media_name) : null,
          media_size: row.media_size == null ? null : Number(row.media_size),
          is_edited: Boolean(row.is_edited),
          edited_at: row.edited_at ? String(row.edited_at) : null,
          sender_name: senderName,
          sender_initials: getEmployeeInitials(senderName),
        } as TaskCommentView;
      })
      .sort((left, right) => left.created_at_ms - right.created_at_ms);

    const byId = new Map(normalizedRows.map((row) => [row.id, row]));
    return normalizedRows.map((row) => ({
      ...row,
      reply_to: row.reply_to_id
        ? (() => {
            const replyTarget = byId.get(String(row.reply_to_id || ''));
            if (!replyTarget) return null;
            return {
              id: replyTarget.id,
              sender_name: replyTarget.sender_name,
              message_text: replyTarget.message_text,
              media_type: replyTarget.media_type || null,
              media_name: replyTarget.media_name || null,
            } as TaskCommentQuote;
          })()
        : null,
    }));
  }, [currentUser.name, isCurrentUserSenderId, senderNameById, task.id]);

  const participantIdsByMessageId = useMemo<Record<string, string[]>>(() => {
    const participants: Record<string, string[]> = {};
    const companyEmployees = employees.map((employee) => ({
      id: String(employee.id || '').trim(),
      name: String(employee.name || '').trim(),
    }));

    const extractMentionedIdsFromText = (text: string): string[] => {
      const normalizedText = String(text || '').toLowerCase();
      return companyEmployees
        .filter((employee) => employee.id && employee.name)
        .filter((employee) => normalizedText.includes(`@${employee.name.toLowerCase()}`))
        .map((employee) => employee.id);
    };

    messages.forEach((message) => {
      const baseParticipantIds = Array.from(
        new Set(
          [
            String(task.assignedTo || '').trim(),
            String(task.assignedBy || '').trim(),
            ...collaboratorIds,
            ...messages.map((row) => String(row.sender_id || '').trim()),
            ...extractMentionedIdsFromText(message.message_text),
          ].filter(Boolean)
        )
      ).filter((participantId) => participantId !== String(message.sender_id || '').trim());

      participants[message.id] = baseParticipantIds;
    });

    return participants;
  }, [collaboratorIds, employees, messages, task.assignedBy, task.assignedTo]);

  const getMessageReceiptStatus = useCallback((message: TaskCommentView): 'sent' | 'delivered' | 'read' => {
    if (!isCurrentUserSenderId(message.sender_id)) {
      return 'sent';
    }

    const receipts = messageReceiptsById[message.id] || [];
    const participantIds = participantIdsByMessageId[message.id] || [];
    const receiptReaderIds = new Set(
      receipts
        .map((receipt) => String(receipt.reader_id || '').trim())
        .filter((readerId) => Boolean(readerId) && readerId !== String(message.sender_id || '').trim())
    );

    if (receiptReaderIds.size === 0) {
      return 'sent';
    }
    if (participantIds.length > 0 && receiptReaderIds.size >= participantIds.length) {
      return 'read';
    }
    return 'delivered';
  }, [isCurrentUserSenderId, messageReceiptsById, participantIdsByMessageId]);

  const proofPhotos = useMemo(() => getTaskProofPhotos(task.proof), [task.proof]);
  const selectedProofPhoto = selectedProofIndex != null ? proofPhotos[selectedProofIndex] ?? null : null;
  const totalProofCount = proofPhotos.length + proofDrafts.length;

  const isHighPriorityTask = normalizedPriority === 'High';
  const requiresFreshCompletionRemark = !isHighPriorityTask;
  const canCompleteTaskThisSession = isHighPriorityTask || remarkAddedThisSession;
  const canPrimaryAssigneeCompleteThisSession = isAssignedWorker && canCompleteTaskThisSession;

  const loadMessageReadReceipts = useCallback(async (messageIds: string[]) => {
    if (messageIds.length === 0) {
      setMessageReceiptsById({});
      return;
    }

    const { data, error } = await supabase
      .from('message_read_receipts')
      .select('id, message_id, reader_id, read_at')
      .in('message_id', messageIds);

    if (error) {
      console.warn('Failed to load task chat read receipts:', error);
      logSupabaseQueryError(error);
      return;
    }

    const nextReceiptsById: Record<string, MessageReadReceipt[]> = {};
    (data || []).forEach((row: any) => {
      const messageId = String(row?.message_id || '').trim();
      if (!messageId) return;
      nextReceiptsById[messageId] = [
        ...(nextReceiptsById[messageId] || []),
        {
          id: String(row?.id || `${messageId}-${row?.reader_id || 'reader'}`),
          message_id: messageId,
          reader_id: String(row?.reader_id || ''),
          read_at: String(row?.read_at || new Date().toISOString()),
        },
      ];
    });

    setMessageReceiptsById((prev) => {
      const next = { ...prev };
      messageIds.forEach((messageId) => {
        if (nextReceiptsById[messageId]?.length) {
          next[messageId] = nextReceiptsById[messageId];
        } else {
          delete next[messageId];
        }
      });
      return next;
    });
  }, []);

  const loadMessageReactions = useCallback(async (messageIds: string[]) => {
    if (messageIds.length === 0) {
      setReactionsByMessageId({});
      return;
    }

    const { data, error } = await supabase
      .from('message_reactions')
      .select('id, message_id, user_id, emoji')
      .in('message_id', messageIds);

    if (error) {
      console.warn('Failed to load message reactions:', error);
      logSupabaseQueryError(error);
      return;
    }

    const nextReactionsById: Record<string, MessageReaction[]> = {};
    (data || []).forEach((row: any) => {
      const messageId = String(row?.message_id || '').trim();
      if (!messageId) return;
      nextReactionsById[messageId] = [
        ...(nextReactionsById[messageId] || []),
        {
          id: String(row?.id || `${messageId}-${row?.user_id || 'user'}-${row?.emoji || 'reaction'}`),
          message_id: messageId,
          user_id: String(row?.user_id || ''),
          emoji: String(row?.emoji || ''),
        },
      ];
    });

    setReactionsByMessageId((prev) => {
      const next = { ...prev };
      messageIds.forEach((messageId) => {
        if (nextReactionsById[messageId]?.length) {
          next[messageId] = nextReactionsById[messageId];
        } else {
          delete next[messageId];
        }
      });
      return next;
    });
  }, []);

  const markMessagesAsRead = useCallback(async (sourceMessages: TaskCommentView[]) => {
    const targetMessages = sourceMessages;
    const unreadMessages = targetMessages.filter((message) => {
      if (isCurrentUserSenderId(message.sender_id)) {
        return false;
      }
      const receipts = messageReceiptsByIdRef.current[message.id] || [];
      return !receipts.some((receipt) => String(receipt.reader_id || '').trim() === String(currentUser.id || '').trim());
    });

    if (unreadMessages.length === 0) {
      return;
    }

    const rows = unreadMessages.map((message) => ({
      message_id: message.id,
      reader_id: currentUser.id,
      read_at: new Date().toISOString(),
    }));

    const { error } = await supabase
      .from('message_read_receipts')
      .upsert(rows, { onConflict: 'message_id,reader_id' });

    if (error) {
      console.warn('Failed to mark task chat messages as read:', error);
      logSupabaseQueryError(error);
      return;
    }

    setMessageReceiptsById((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        next[row.message_id] = [
          ...(next[row.message_id] || []).filter(
            (receipt) => String(receipt.reader_id || '').trim() !== String(currentUser.id || '').trim()
          ),
          {
            id: `${row.message_id}-${currentUser.id}`,
            message_id: row.message_id,
            reader_id: currentUser.id,
            read_at: row.read_at,
          },
        ];
      });
      return next;
    });
  }, [currentUser.id, isCurrentUserSenderId]);

  useEffect(() => {
    markMessagesAsReadRef.current = markMessagesAsRead;
  }, [markMessagesAsRead]);

  const loadTaskMessages = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent ?? false;
    const loadSeq = ++taskChatLoadSeqRef.current;
    const startedAt = performance.now();
    if (!silent) {
      setLoadingMessages(true);
    }

    const { data, error } = await supabase
      .from('task_comments')
      .select(TASK_COMMENT_COLUMNS)
      .eq('task_id', task.id)
      .order('created_at', { ascending: false })
      .limit(TASK_CHAT_PAGE_SIZE);

    if (error) {
      console.warn('Failed to load task messages:', error);
      logSupabaseQueryError(error);
      if (!silent) {
        setLoadingMessages(false);
      }
      return;
    }

    if (loadSeq !== taskChatLoadSeqRef.current) {
      logTaskChatPerf('ignored stale initial load', { taskId: task.id, loadSeq });
      return;
    }

    const normalized = normalizeTaskComments((data || []) as TaskComment[]);
    setHasOlderMessages((data || []).length === TASK_CHAT_PAGE_SIZE);
    setMessages((prev) => {
      if (prev.length === 0) return normalized;
      const fetchedIds = new Set(normalized.map((message) => message.id));
      const pendingMessages = prev.filter((message) => !fetchedIds.has(message.id));
      if (pendingMessages.length === 0) return normalized;
      return mergeTaskMessages(normalized, pendingMessages);
    });
    void loadMessageReactions(normalized.map((message) => message.id));
    if (!silent) {
      setLoadingMessages(false);
    }
    logTaskChatPerf('initial page loaded', {
      taskId: task.id,
      count: normalized.length,
      ms: Math.round(performance.now() - startedAt),
    });
  }, [loadMessageReactions, normalizeTaskComments, task.id]);

  useEffect(() => {
    loadTaskMessagesRef.current = loadTaskMessages;
  }, [loadTaskMessages]);

  const backfillTaskMessages = useCallback(async () => {
    const latestMessage = [...messagesRef.current].reverse().find((message) => !message.optimistic);
    const startedAt = performance.now();

    let query = supabase
      .from('task_comments')
      .select(TASK_COMMENT_COLUMNS)
      .eq('task_id', task.id);

    if (latestMessage?.created_at) {
      query = query.gt('created_at', latestMessage.created_at).order('created_at', { ascending: true }).limit(50);
    } else {
      query = query.order('created_at', { ascending: false }).limit(50);
    }

    const { data, error } = await query;
    if (error) {
      console.warn('Failed to backfill task messages:', error);
      logSupabaseQueryError(error);
      await loadTaskMessagesRef.current?.({ silent: true });
      return;
    }

    const normalized = normalizeTaskComments((data || []) as TaskComment[]);
    if (normalized.length === 0) {
      logTaskChatPerf('visibility backfill empty', {
        taskId: task.id,
        since: latestMessage?.created_at || null,
        ms: Math.round(performance.now() - startedAt),
      });
      return;
    }

    setMessages((prev) => mergeTaskMessages(prev, normalized));
    void loadMessageReactions(normalized.map((message) => message.id));
    void loadMessageReadReceipts(normalized.map((message) => message.id));
    const unreadIncoming = normalized.filter(
      (message) => !isCurrentUserSenderIdRef.current?.(message.sender_id)
    );
    if (unreadIncoming.length > 0) {
      void markMessagesAsReadRef.current?.(unreadIncoming);
    }
    logTaskChatPerf('visibility backfill loaded', {
      taskId: task.id,
      since: latestMessage?.created_at || null,
      count: normalized.length,
      ms: Math.round(performance.now() - startedAt),
    });
  }, [loadMessageReactions, loadMessageReadReceipts, normalizeTaskComments, task.id]);

  useEffect(() => {
    backfillTaskMessagesRef.current = backfillTaskMessages;
  }, [backfillTaskMessages]);

  const loadOlderTaskMessages = useCallback(async () => {
    if (loadingOlderMessages || !hasOlderMessages) return;
    const oldest = messagesRef.current[0];
    if (!oldest?.created_at) return;

    const startedAt = performance.now();
    setLoadingOlderMessages(true);
    const { data, error } = await supabase
      .from('task_comments')
      .select(TASK_COMMENT_COLUMNS)
      .eq('task_id', task.id)
      .lt('created_at', oldest.created_at)
      .order('created_at', { ascending: false })
      .limit(TASK_CHAT_PAGE_SIZE);

    setLoadingOlderMessages(false);

    if (error) {
      console.warn('Failed to load older task messages:', error);
      logSupabaseQueryError(error);
      return;
    }

    const normalized = normalizeTaskComments((data || []) as TaskComment[]);
    setHasOlderMessages((data || []).length === TASK_CHAT_PAGE_SIZE);
    setMessages((prev) => mergeTaskMessages(prev, normalized, 'prepend'));
    void loadMessageReactions(normalized.map((message) => message.id));
    void loadMessageReadReceipts(normalized.map((message) => message.id));
    logTaskChatPerf('older page loaded', {
      taskId: task.id,
      count: normalized.length,
      ms: Math.round(performance.now() - startedAt),
    });
  }, [hasOlderMessages, loadMessageReadReceipts, loadMessageReactions, loadingOlderMessages, normalizeTaskComments, task.id]);

  // --- Effects ---
  useEffect(() => {
    setEditDescription(task.description);
    setActiveTab('chat');
    setEditAssigneeId(task.assignedTo || 'none');
    setEditDeadline(formatDateTimeForInput(task.deadline));
    setEditRequirePhoto(Boolean(task.requirePhoto));
    setEditIsHighPriority(normalizedPriority === 'High');
    setEditTaskType(normalizedTaskType);
    setEditRecurrenceFrequency(normalizedTaskType === 'recurring' ? (normalizedRecurrence || '') : '');
    setEditRecurrenceTime(normalizedTaskType === 'recurring' ? (recurrenceTime || getRoundedFiveMinuteTime()) : getRoundedFiveMinuteTime());
    setIsEditing(false);
  }, [task.id, task.description, task.assignedTo, task.deadline, task.requirePhoto, normalizedPriority, normalizedTaskType, normalizedRecurrence, recurrenceTime]);

  useEffect(() => {
    setRemarkAddedThisSession(false);
    setSelectedProofIndex(null);
    setUploadingProofDraftIds([]);
    setMessages([]);
    setMessageReceiptsById({});
    setReactionsByMessageId({});
    setLoadingMessages(true);
    setLoadingOlderMessages(false);
    setHasOlderMessages(false);
    setReplyingToMessage(null);
    setEditingMessage(null);
    setMessageActionMenu(null);
    setReactionViewerMessageId(null);
    setHighlightedMessageId(null);
    setShowAttachmentMenu(false);
    hasAnchoredInitialMessagesRef.current = false;
    previousMessageLengthRef.current = 0;
    isNearChatBottomRef.current = true;
    setProofDrafts((prev) => {
      clearProofDrafts(prev);
      return [];
    });
    setSelectedChatMedia((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  }, [task.id]);

  useEffect(() => {
    proofDraftsRef.current = proofDrafts;
  }, [proofDrafts]);

  useEffect(() => {
    return () => {
      clearProofDrafts(proofDraftsRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isEditableElement = (target: EventTarget | null): boolean => {
      if (!(target instanceof HTMLElement)) return false;
      const tagName = target.tagName.toLowerCase();
      return tagName === 'input' || tagName === 'textarea' || target.isContentEditable;
    };

    const syncKeyboardState = () => {
      const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;
      const focusedEditable = isEditableElement(document.activeElement);
      const viewport = window.visualViewport;
      const keyboardLikelyOpen = Boolean(
        viewport &&
        isMobileViewport &&
        window.innerHeight - viewport.height - viewport.offsetTop > 80
      );

      setIsTaskKeyboardActive(isMobileViewport && activeTab === 'chat' && (focusedEditable || keyboardLikelyOpen));
    };

    const handleFocusOut = () => {
      window.setTimeout(syncKeyboardState, 120);
    };

    syncKeyboardState();
    window.addEventListener('focusin', syncKeyboardState);
    window.addEventListener('focusout', handleFocusOut);
    window.addEventListener('resize', syncKeyboardState);
    window.visualViewport?.addEventListener('resize', syncKeyboardState);
    window.visualViewport?.addEventListener('scroll', syncKeyboardState);

    return () => {
      window.removeEventListener('focusin', syncKeyboardState);
      window.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('resize', syncKeyboardState);
      window.visualViewport?.removeEventListener('resize', syncKeyboardState);
      window.visualViewport?.removeEventListener('scroll', syncKeyboardState);
    };
  }, [activeTab]);

  useEffect(() => {
    messageIdsRef.current = messages.map((message) => message.id);
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    messageReceiptsByIdRef.current = messageReceiptsById;
  }, [messageReceiptsById]);

  const messageIdsKey = useMemo(
    () => messages.map((message) => message.id).join(','),
    [messages]
  );

  useEffect(() => {
    selectedChatMediaRef.current = selectedChatMedia;
  }, [selectedChatMedia]);

  useEffect(() => {
    return () => {
      if (selectedChatMediaRef.current?.previewUrl) {
        URL.revokeObjectURL(selectedChatMediaRef.current.previewUrl);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (loadingMessages || messages.length === 0 || hasAnchoredInitialMessagesRef.current) {
      return;
    }

    scrollTaskMessagesToBottom('instant');
    const frameId = window.requestAnimationFrame(() => {
      scrollTaskMessagesToBottom('instant');
    });
    const timeoutId = window.setTimeout(() => {
      scrollTaskMessagesToBottom('instant');
    }, 80);

    hasAnchoredInitialMessagesRef.current = true;
    previousMessageLengthRef.current = messages.length;
    isNearChatBottomRef.current = true;

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [loadingMessages, messages.length, scrollTaskMessagesToBottom]);

  useLayoutEffect(() => {
    if (!hasAnchoredInitialMessagesRef.current) {
      previousMessageLengthRef.current = messages.length;
      return;
    }

    const previousLength = previousMessageLengthRef.current;
    previousMessageLengthRef.current = messages.length;
    if (messages.length <= previousLength) {
      return;
    }

    const shouldForceOwnMessageScroll = pendingOwnMessageScrollRef.current;
    if (!shouldForceOwnMessageScroll && !isNearChatBottomRef.current) {
      return;
    }

    pendingOwnMessageScrollRef.current = false;
    scrollTaskMessagesToBottom(shouldForceOwnMessageScroll ? 'instant' : 'smooth');

    if (!shouldForceOwnMessageScroll) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      scrollTaskMessagesToBottom('instant');
    });
    const timeoutId = window.setTimeout(() => {
      scrollTaskMessagesToBottom('instant');
    }, 120);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(timeoutId);
    };
  }, [messages.length, scrollTaskMessagesToBottom]);

  useEffect(() => {
    if (!chatScrollSignal) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const target = remarkInputRef.current || remarksScrollRef.current;
      if (!target) return;

      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (remarkInputRef.current && !readOnly) {
        remarkInputRef.current.focus({ preventScroll: true });
      }
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [chatScrollSignal, task.id, readOnly]);

  useEffect(() => {
    if (!mentionMenuOpen) return;

    let isCancelled = false;
    const timer = window.setTimeout(async () => {
      setMentionLoading(true);
      const companyId = String(currentUser.company_id || '').trim();
      const queryText = mentionQuery.trim();

      try {
        let supabaseCandidates: MentionCandidate[] = [];
        if (companyId) {
          let dbQuery = supabase
            .from('employees')
            .select('id, name, role, mobile')
            .eq('company_id', companyId)
            .neq('id', currentUser.id)
            .order('name', { ascending: true })
            .limit(8);

          if (queryText) {
            dbQuery = dbQuery.ilike('name', `%${queryText}%`);
          }

          const { data, error } = await dbQuery;
          if (!error && Array.isArray(data)) {
            supabaseCandidates = data.map((row: any) => ({
              id: String(row.id || ''),
              name: String(row.name || 'User').trim(),
            }));
          }
        }

        const localFallback = mentionableMembers.filter((member) => {
          if (!queryText) return true;
          return member.name.toLowerCase().includes(queryText.toLowerCase());
        });

        const merged = [...supabaseCandidates, ...localFallback]
          .filter((item) => item.id && item.name)
          .reduce<MentionCandidate[]>((acc, candidate) => {
            if (acc.some((existing) => existing.id === candidate.id)) return acc;
            acc.push(candidate);
            return acc;
          }, [])
          .slice(0, 8);

        if (!isCancelled) {
          setMentionCandidates(merged);
          setActiveMentionIndex(0);
        }
      } finally {
        if (!isCancelled) {
          setMentionLoading(false);
        }
      }
    }, 120);

    return () => {
      isCancelled = true;
      window.clearTimeout(timer);
    };
  }, [mentionMenuOpen, mentionQuery, mentionableMembers, currentUser.company_id, currentUser.id]);

  useEffect(() => {
    setShowExtensionInput(false);
    setExtensionDate(formatDateTimeForInput(requestedDueDate ?? task.deadline));
  }, [task.id, requestedDueDate, task.deadline]);

  useEffect(() => {
    if (!showExtensionInput || readOnly || !canRequestExtension) return;

    const input = extensionInputRef.current;
    if (!input) return;

    let lastValue = input.value;
    let interval: ReturnType<typeof window.setInterval> | null = null;

    const checkValue = () => {
      const currentValue = input.value;
      if (currentValue !== lastValue) {
        lastValue = currentValue;
        setExtensionDate(currentValue || '');
      }
    };

    const observer = new MutationObserver(checkValue);
    observer.observe(input, {
      attributes: true,
      attributeFilter: ['value']
    });

    const onFocus = () => {
      lastValue = input.value;
      if (interval) window.clearInterval(interval);
      interval = window.setInterval(checkValue, 300);
    };

    const onFocusOut = () => {
      if (interval) {
        window.clearInterval(interval);
        interval = null;
      }
      window.setTimeout(checkValue, 100);
    };

    input.addEventListener('focus', onFocus);
    input.addEventListener('focusout', onFocusOut);

    return () => {
      if (interval) window.clearInterval(interval);
      observer.disconnect();
      input.removeEventListener('focus', onFocus);
      input.removeEventListener('focusout', onFocusOut);
    };
  }, [showExtensionInput, readOnly, canRequestExtension]);

  useEffect(() => {
    let isCancelled = false;

    const resolveExtensionApproverId = async () => {
      const companyId = String(currentUser.company_id || task.company_id || '').trim();
      const localCreatorId = getTaskCreatorId(task as unknown as Record<string, unknown>);

      if (localCreatorId) {
        if (!isCancelled) {
          setExtensionApproverId(localCreatorId);
        }
        return;
      }

      let taskQuery = supabase.from('tasks').select('*').eq('id', task.id).limit(1);
      if (companyId) {
        taskQuery = taskQuery.eq('company_id', companyId);
      }

      const { data: taskRow, error: taskError } = await taskQuery.maybeSingle();
      if (taskError) {
        console.warn('Failed to resolve extension reviewer from task creator:', taskError);
        logSupabaseQueryError(taskError);
        if (!isCancelled) {
          setExtensionApproverId(null);
        }
        return;
      }

      if (!isCancelled) {
        setExtensionApproverId(getTaskCreatorId((taskRow || null) as Record<string, unknown>) || null);
      }
    };

    void resolveExtensionApproverId();

    return () => {
      isCancelled = true;
    };
  }, [currentUser.company_id, task.company_id, task.id]);

  useEffect(() => {
    setMentionMenuOpen(false);
    setMentionCandidates([]);
    setMentionQuery('');
    setMentionStartIndex(null);
    setActiveMentionIndex(0);
    setSelectedMentions([]);
  }, [task.id]);

  useEffect(() => {
    void loadTaskMessages();
  }, [loadTaskMessages]);

  useEffect(() => {
    if (messages.length === 0) {
      setMessageReceiptsById({});
      return;
    }

    const idsNeedingReceipts = messages
      .map((message) => message.id)
      .filter((messageId) => !messageReceiptsByIdRef.current[messageId]);
    if (idsNeedingReceipts.length > 0) {
      void loadMessageReadReceipts(idsNeedingReceipts);
    }
    void markMessagesAsRead(messages);
  }, [loadMessageReadReceipts, markMessagesAsRead, messages]);

  useEffect(() => {
    const closeMenus = () => {
      setShowAttachmentMenu(false);
      setMessageActionMenu(null);
    };

    window.addEventListener('click', closeMenus);
    return () => {
      window.removeEventListener('click', closeMenus);
    };
  }, []);

  useEffect(() => {
    if (!showAttachmentMenu || editingMessage) {
      setAttachmentMenuPosition(null);
      return;
    }

    updateAttachmentMenuPosition();
    const syncPosition = () => updateAttachmentMenuPosition();
    window.addEventListener('resize', syncPosition);
    window.addEventListener('scroll', syncPosition, true);
    window.visualViewport?.addEventListener('resize', syncPosition);
    window.visualViewport?.addEventListener('scroll', syncPosition);

    return () => {
      window.removeEventListener('resize', syncPosition);
      window.removeEventListener('scroll', syncPosition, true);
      window.visualViewport?.removeEventListener('resize', syncPosition);
      window.visualViewport?.removeEventListener('scroll', syncPosition);
    };
  }, [editingMessage, showAttachmentMenu, updateAttachmentMenuPosition]);

  useEffect(() => {
    const highlightTimeout = highlightedMessageId
      ? window.setTimeout(() => setHighlightedMessageId(null), 1600)
      : null;

    return () => {
      if (highlightTimeout) {
        window.clearTimeout(highlightTimeout);
      }
    };
  }, [highlightedMessageId]);

  const normalizeRealtimeMessageRow = useCallback((row: Partial<TaskComment> | null | undefined): TaskCommentView | null => {
    if (!row?.id) return null;
    const normalized = normalizeTaskComments([row as TaskComment])[0];
    if (!normalized) return null;
    if (normalized.reply_to_id && !normalized.reply_to) {
      const quoted = messagesRef.current.find((message) => message.id === normalized.reply_to_id);
      if (quoted) {
        normalized.reply_to = {
          id: quoted.id,
          sender_name: quoted.sender_name,
          message_text: quoted.message_text,
          media_type: quoted.media_type || null,
          media_name: quoted.media_name || null,
        };
      }
    }
    return normalized;
  }, [normalizeTaskComments]);

  useEffect(() => {
    normalizeRealtimeMessageRowRef.current = normalizeRealtimeMessageRow;
  }, [normalizeRealtimeMessageRow]);

  useEffect(() => {
    if (taskChatChannelRef.current) {
      console.warn('[task-chat] duplicate subscription prevented', { taskId: task.id });
      void supabase.removeChannel(taskChatChannelRef.current);
      taskChatChannelRef.current = null;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void backfillTaskMessagesRef.current?.();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const channel = supabase
      .channel(`chat-${task.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'task_comments',
          filter: `task_id=eq.${task.id}`,
        },
        (payload: any) => {
          taskChatEventCountRef.current += 1;
          const eventType = String(payload?.eventType || '').toUpperCase();
          logTaskChatPerf('realtime task_comments', {
            taskId: task.id,
            eventType,
            count: taskChatEventCountRef.current,
          });

          const incoming = normalizeRealtimeMessageRowRef.current?.(payload?.new);
          if (!incoming) return;
          setMessages((prev) => mergeTaskMessages(prev, [incoming]));

          if (!isCurrentUserSenderIdRef.current?.(incoming.sender_id)) {
            window.setTimeout(() => {
              void markMessagesAsReadRef.current?.([incoming]);
            }, 0);
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_read_receipts',
        },
        (payload: any) => {
          taskChatEventCountRef.current += 1;
          const receiptMessageId = String(payload?.new?.message_id || payload?.old?.message_id || '').trim();
          if (!receiptMessageId || !messageIdsRef.current.includes(receiptMessageId)) {
            return;
          }
          const eventType = String(payload?.eventType || '').toUpperCase();
          logTaskChatPerf('realtime receipt', {
            taskId: task.id,
            eventType,
            count: taskChatEventCountRef.current,
          });

          setMessageReceiptsById((prev) => {
            const current = prev[receiptMessageId] || [];
            if (eventType === 'DELETE') {
              const receiptId = String(payload?.old?.id || '').trim();
              const filtered = receiptId
                ? current.filter((receipt) => receipt.id !== receiptId)
                : current.filter((receipt) => String(receipt.reader_id || '') !== String(payload?.old?.reader_id || ''));
              if (filtered.length === current.length) return prev;
              const next = { ...prev };
              if (filtered.length > 0) next[receiptMessageId] = filtered;
              else delete next[receiptMessageId];
              return next;
            }

            const nextReceipt: MessageReadReceipt = {
              id: String(payload?.new?.id || `${receiptMessageId}-${payload?.new?.reader_id || 'reader'}`),
              message_id: receiptMessageId,
              reader_id: String(payload?.new?.reader_id || ''),
              read_at: String(payload?.new?.read_at || new Date().toISOString()),
            };
            const existingIndex = current.findIndex(
              (receipt) => receipt.id === nextReceipt.id || receipt.reader_id === nextReceipt.reader_id
            );
            if (existingIndex >= 0 && current[existingIndex].read_at === nextReceipt.read_at) {
              return prev;
            }
            const merged = [...current];
            if (existingIndex >= 0) merged[existingIndex] = nextReceipt;
            else merged.push(nextReceipt);
            return { ...prev, [receiptMessageId]: merged };
          });
        }
      )
      .subscribe((status) => {
        logTaskChatPerf('subscription status', { taskId: task.id, channel: `chat-${task.id}`, status });
        if (status === 'SUBSCRIBED') {
          void backfillTaskMessagesRef.current?.();
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          void loadTaskMessagesRef.current?.({ silent: true });
        }
      });

    taskChatChannelRef.current = channel;

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (taskChatChannelRef.current === channel) {
        taskChatChannelRef.current = null;
      }
      void supabase.removeChannel(channel);
    };
  }, [task.id]);

  useEffect(() => {
    const messageIds = messageIdsKey.split(',').filter(Boolean);
    if (messageIds.length === 0) {
      setReactionsByMessageId({});
      return;
    }

    void loadMessageReactions(messageIds);
  }, [loadMessageReactions, messageIdsKey]);

  useEffect(() => {
    const channel = supabase
      .channel(`task-reactions-${task.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'message_reactions',
        },
        (payload: any) => {
          const eventType = String(payload?.eventType || '').toUpperCase();
          const row = eventType === 'DELETE' ? payload?.old || {} : payload?.new || {};
          const messageId = String(row.message_id || '').trim();
          const reactionId = String(row.id || '').trim();

          if (eventType === 'DELETE') {
            if (messageId && !messageIdsRef.current.includes(messageId)) {
              return;
            }

            setReactionsByMessageId((prev) => {
              const next = { ...prev };
              const targetMessageIds = messageId ? [messageId] : Object.keys(next);
              let changed = false;

              targetMessageIds.forEach((targetMessageId) => {
                const current = next[targetMessageId] || [];
                const filtered = current.filter((reaction) => {
                  if (reactionId) {
                    return reaction.id !== reactionId;
                  }
                  return !(
                    String(reaction.user_id || '').trim() === String(row.user_id || '').trim() &&
                    String(reaction.emoji || '').trim() === String(row.emoji || '').trim()
                  );
                });
                if (filtered.length !== current.length) {
                  changed = true;
                  if (filtered.length > 0) {
                    next[targetMessageId] = filtered;
                  } else {
                    delete next[targetMessageId];
                  }
                }
              });

              return changed ? next : prev;
            });
            return;
          }

          if (!messageId || !messageIdsRef.current.includes(messageId)) {
            return;
          }

          const reaction: MessageReaction = {
            id: reactionId || `${messageId}-${row.user_id || 'user'}-${row.emoji || 'reaction'}`,
            message_id: messageId,
            user_id: String(row.user_id || ''),
            emoji: String(row.emoji || ''),
          };

          setReactionsByMessageId((prev) => {
            const existing = prev[messageId] || [];
            if (existing.some((item) => item.id === reaction.id)) {
              return prev;
            }
            return {
              ...prev,
              [messageId]: [...existing, reaction],
            };
          });
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [task.id]);

  useEffect(() => {
    if (!reactionViewerMessageId) {
      return;
    }

    void fetchReactionNames(reactionViewerMessageId);
  }, [fetchReactionNames, reactionViewerMessageId, reactionsByMessageId]);

  useEffect(() => {
    if (!isEditing || typeof window === 'undefined') {
      setEditModalKeyboardInset(0);
      setEditModalVisibleHeight(null);
      return;
    }

    const isMobileViewport = () => window.innerWidth < 640;
    const viewport = window.visualViewport;

    const syncViewportInsets = () => {
      if (!isMobileViewport() || !viewport) {
        setEditModalKeyboardInset(0);
        setEditModalVisibleHeight(null);
        return;
      }

      const keyboardInset = Math.max(
        0,
        Math.round(window.innerHeight - viewport.height - viewport.offsetTop)
      );

      setEditModalKeyboardInset(keyboardInset);
      setEditModalVisibleHeight(Math.round(viewport.height + viewport.offsetTop));
    };

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!editModalScrollRef.current?.contains(target)) return;

      window.setTimeout(() => {
        target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 80);
    };

    syncViewportInsets();
    viewport?.addEventListener('resize', syncViewportInsets);
    viewport?.addEventListener('scroll', syncViewportInsets);
    window.addEventListener('resize', syncViewportInsets);
    window.addEventListener('focusin', handleFocusIn);

    return () => {
      viewport?.removeEventListener('resize', syncViewportInsets);
      viewport?.removeEventListener('scroll', syncViewportInsets);
      window.removeEventListener('resize', syncViewportInsets);
      window.removeEventListener('focusin', handleFocusIn);
    };
  }, [isEditing]);

  // --- Handlers ---
  const toggleReaction = async (messageId: string) => {
    const normalizedMessageId = String(messageId || '').trim();
    if (!normalizedMessageId || !currentEmployeeId) {
      return;
    }

    const existing = reactionsByMessageId[normalizedMessageId]?.find(
      (reaction) => reaction.user_id === currentEmployeeId && reaction.emoji === '👍'
    );

    if (existing) {
      const { error } = await supabase
        .from('message_reactions')
        .delete()
        .eq('id', existing.id);

      if (error) {
        console.warn('Failed to remove message reaction:', error);
        logSupabaseQueryError(error);
        return;
      }

      setReactionsByMessageId((prev) => {
        const current = prev[normalizedMessageId] || [];
        const filtered = current.filter((reaction) => reaction.id !== existing.id);
        if (filtered.length === current.length) {
          return prev;
        }
        const next = { ...prev };
        if (filtered.length > 0) {
          next[normalizedMessageId] = filtered;
        } else {
          delete next[normalizedMessageId];
        }
        return next;
      });
      return;
    }

    const { data, error } = await supabase
      .from('message_reactions')
      .insert({
        message_id: normalizedMessageId,
        user_id: currentEmployeeId,
        emoji: '👍',
      })
      .select('*')
      .single();

    if (error) {
      console.warn('Failed to add message reaction:', error);
      logSupabaseQueryError(error);
      return;
    }

    const row = data as any;
    const reaction: MessageReaction = {
      id: String(row?.id || `${normalizedMessageId}-${currentEmployeeId}-like`),
      message_id: normalizedMessageId,
      user_id: String(row?.user_id || currentEmployeeId),
      emoji: String(row?.emoji || '👍'),
    };

    setReactionsByMessageId((prev) => {
      const existingReactions = prev[normalizedMessageId] || [];
      if (existingReactions.some((item) => item.id === reaction.id)) {
        return prev;
      }
      return {
        ...prev,
        [normalizedMessageId]: [...existingReactions, reaction],
      };
    });
  };

  const handleReactionToggleTouchEnd = (
    event: React.TouchEvent<HTMLButtonElement>,
    messageId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    void toggleReaction(messageId);
  };

  const openReactionViewer = (messageId: string) => {
    const normalizedMessageId = String(messageId || '').trim();
    if (!normalizedMessageId) return;
    setReactionViewerMessageId(normalizedMessageId);
    void fetchReactionNames(normalizedMessageId);
  };

  const handleReactionPillClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    messageId: string
  ) => {
    event.stopPropagation();
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      openReactionViewer(messageId);
      return;
    }
    void toggleReaction(messageId);
  };

  const handleReactionPillTouchEnd = (
    event: React.TouchEvent<HTMLButtonElement>,
    messageId: string
  ) => {
    event.preventDefault();
    event.stopPropagation();
    openReactionViewer(messageId);
  };

  const showReactionTooltip = (
    event: React.MouseEvent<HTMLButtonElement> | React.FocusEvent<HTMLButtonElement>,
    messageId: string
  ) => {
    if (typeof window !== 'undefined' && window.innerWidth < 640) {
      return;
    }

    const normalizedMessageId = String(messageId || '').trim();
    if (!normalizedMessageId) return;

    const rect = event.currentTarget.getBoundingClientRect();
    setReactionTooltip({
      messageId: normalizedMessageId,
      left: rect.left + rect.width / 2,
      top: rect.top - 10,
    });
    void fetchReactionNames(normalizedMessageId);
  };

  const hideReactionTooltip = () => {
    setReactionTooltip(null);
  };

  const reactionViewerNames = reactionViewerMessageId
    ? getReactionDisplayNames(reactionViewerMessageId)
    : [];

  const reactionTooltipNames = reactionTooltip
    ? getReactionDisplayNames(reactionTooltip.messageId)
    : [];

  const clearProofDrafts = (drafts: ProofDraft[]) => {
    drafts.forEach((draft) => {
      URL.revokeObjectURL(draft.previewUrl);
    });
  };

  const openMessageActionMenu = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    message: TaskCommentView
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const point =
      'touches' in event && event.touches[0]
        ? { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY }
        : 'changedTouches' in event && event.changedTouches[0]
        ? { clientX: event.changedTouches[0].clientX, clientY: event.changedTouches[0].clientY }
        : 'clientX' in event
        ? { clientX: event.clientX, clientY: event.clientY }
        : { clientX: window.innerWidth / 2, clientY: window.innerHeight / 2 };

    setMessageActionMenu({
      messageId: message.id,
      isOwn: isCurrentUserSenderId(message.sender_id),
      canEdit: isMessageWithinEditWindow(message.created_at),
      x: point.clientX,
      y: point.clientY,
    });
  };

  const openMessageActionMenuFromButton = (
    event: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>,
    message: TaskCommentView
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    setMessageActionMenu({
      messageId: message.id,
      isOwn: isCurrentUserSenderId(message.sender_id),
      canEdit: isMessageWithinEditWindow(message.created_at),
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const beginEditingMessage = (message: TaskCommentView) => {
    setEditingMessage(message);
    setReplyingToMessage(null);
    setNewRemark(message.message_text || '');
    setMessageActionMenu(null);
    requestAnimationFrame(() => {
      remarkInputRef.current?.focus();
    });
  };

  const handleMessageTouchStart = (event: React.TouchEvent<HTMLDivElement>, message: TaskCommentView) => {
    const touch = event.touches[0];
    touchGestureRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      replyTriggered: false,
    };

    if (longPressTimeoutRef.current) {
      window.clearTimeout(longPressTimeoutRef.current);
    }

    longPressTimeoutRef.current = window.setTimeout(() => {
      openMessageActionMenu(event, message);
    }, 420);
  };

  const handleMessageTouchMove = (event: React.TouchEvent<HTMLDivElement>, message: TaskCommentView) => {
    const touch = event.touches[0];
    const gesture = touchGestureRef.current;
    if (!gesture) {
      return;
    }

    const deltaX = touch.clientX - gesture.startX;
    const deltaY = Math.abs(touch.clientY - gesture.startY);
    if (!gesture.replyTriggered && deltaX > 72 && deltaY < 48) {
      gesture.replyTriggered = true;
      if (longPressTimeoutRef.current) {
        window.clearTimeout(longPressTimeoutRef.current);
      }
      setReplyingToMessage(message);
      setEditingMessage(null);
      toast.success('Replying to message');
    }
  };

  const handleMessageTouchEnd = () => {
    if (longPressTimeoutRef.current) {
      window.clearTimeout(longPressTimeoutRef.current);
      longPressTimeoutRef.current = null;
    }
    touchGestureRef.current = null;
  };

  const handleChatMediaSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const originalFile = event.target.files?.[0];
    event.target.value = '';
    if (!originalFile) {
      return;
    }

    if (originalFile.size > MAX_CHAT_MEDIA_BYTES) {
      toast.error('Attachments must be 50 MB or smaller');
      return;
    }

    const startedAt = performance.now();
    const file = await compressImageFile(originalFile).catch((error) => {
      console.warn('Image compression failed; using original file:', error);
      return originalFile;
    });
    logTaskChatPerf('media selected', {
      taskId: task.id,
      originalBytes: originalFile.size,
      uploadBytes: file.size,
      compressed: file.size < originalFile.size,
      ms: Math.round(performance.now() - startedAt),
    });

    setSelectedChatMedia((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      const mediaType = getMediaTypeFromFile(file);
      return {
        id: `${task.id}-${Date.now()}`,
        file,
        mediaType,
        name: file.name,
        size: file.size,
        previewUrl: mediaType === 'file' ? null : URL.createObjectURL(file),
      };
    });
    setShowAttachmentMenu(false);
  };

  const clearSelectedChatMedia = () => {
    setSelectedChatMedia((prev) => {
      if (prev?.previewUrl) {
        URL.revokeObjectURL(prev.previewUrl);
      }
      return null;
    });
  };

  const scrollToMessage = (messageId: string) => {
    const target = messageRefs.current[messageId];
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedMessageId(messageId);
  };

  const renderChatMediaPreview = (message: TaskCommentView, compact = false) => {
    const mediaUrl = String(message.media_url || '').trim();
    const mediaType = message.media_type || null;
    if (!mediaType || !mediaUrl) {
      return null;
    }

    if (mediaType === 'image') {
      return (
        <button
          type="button"
          onClick={() => setMediaPreview({ url: mediaUrl, type: mediaType, name: message.media_name || 'Photo' })}
          className={`overflow-hidden rounded-2xl ${compact ? 'max-w-[150px]' : 'max-w-xs'} border border-black/5 bg-white/10 text-left`}
        >
          <img src={mediaUrl} alt={message.media_name || 'Chat image'} className="max-h-64 w-full object-cover" />
        </button>
      );
    }

    if (mediaType === 'video') {
      return (
        <button
          type="button"
          onClick={() => setMediaPreview({ url: mediaUrl, type: mediaType, name: message.media_name || 'Video' })}
          className={`relative overflow-hidden rounded-2xl ${compact ? 'max-w-[150px]' : 'max-w-xs'} border border-black/5 bg-slate-900 text-left`}
        >
          <video src={mediaUrl} className="max-h-64 w-full object-cover opacity-80" />
          <span className="absolute inset-0 flex items-center justify-center">
            <Play className="h-10 w-10 rounded-full bg-white/90 p-2 text-slate-900" />
          </span>
        </button>
      );
    }

    if (mediaType === 'audio') {
      return (
        <div className="rounded-2xl border border-black/5 bg-white/70 px-3 py-2">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-600">
            <Music4 className="h-4 w-4" />
            <span className="truncate">{message.media_name || 'Audio note'}</span>
          </div>
          <audio controls src={mediaUrl} className="w-full" />
        </div>
      );
    }

    return (
      <a
        href={mediaUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-3 rounded-2xl border border-black/5 bg-white/70 px-3 py-3"
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <FileText className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-slate-800">{message.media_name || 'Attachment'}</span>
          <span className="block text-xs text-slate-500">{formatFileSize(message.media_size)}</span>
        </span>
      </a>
    );
  };

  const openProofPicker = () => {
    if (totalProofCount >= MAX_TASK_PROOF_PHOTOS) {
      toast.warning('Maximum 5 photos reached');
      return;
    }

    proofInputRef.current?.click();
  };

  const handleProofSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';

    if (files.length === 0) {
      return;
    }

    const validFiles = files.filter((file) => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      toast.error('Only image files can be uploaded');
    }

    const remainingSlots = MAX_TASK_PROOF_PHOTOS - totalProofCount;
    if (remainingSlots <= 0) {
      toast.warning('Maximum 5 photos reached');
      return;
    }

    if (validFiles.length > remainingSlots) {
      toast.warning(`Only ${remainingSlots} more photo${remainingSlots === 1 ? '' : 's'} can be added`);
    }

    const nextDrafts = validFiles.slice(0, remainingSlots).map((file, index) => ({
      id: `${task.id}-${Date.now()}-${index}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    if (nextDrafts.length === 0) {
      return;
    }

    setProofDrafts((prev) => [...prev, ...nextDrafts]);
  };

  const removeProofDraft = (draftId: string) => {
    setProofDrafts((prev) => {
      const draftToRemove = prev.find((draft) => draft.id === draftId);
      if (draftToRemove) {
        URL.revokeObjectURL(draftToRemove.previewUrl);
      }
      return prev.filter((draft) => draft.id !== draftId);
    });
    setUploadingProofDraftIds((prev) => prev.filter((id) => id !== draftId));
  };

  const uploadProofDraftBatch = async (): Promise<ProofPhoto[]> => {
    if (proofDrafts.length === 0) {
      return [];
    }

    const batchTimestamp = Date.now();
    const proofPayload: ProofPhoto[] = [];

    for (let index = 0; index < proofDrafts.length; index += 1) {
      const draft = proofDrafts[index];
      setUploadingProofDraftIds((prev) => [...prev, draft.id]);

      const fileName = `task-proof-${task.id}-${batchTimestamp}-${index}.jpg`;
      const { error } = await supabase.storage
        .from('task-proofs')
        .upload(fileName, draft.file, {
          contentType: draft.file.type || 'image/jpeg',
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        throw new Error(`Failed to upload photo ${index + 1}: ${error.message}`);
      }

      const {
        data: { publicUrl },
      } = supabase.storage.from('task-proofs').getPublicUrl(fileName);
      proofPayload.push({ imageUrl: publicUrl, timestamp: Date.now() });

      setUploadingProofDraftIds((prev) => prev.filter((id) => id !== draft.id));
    }

    return proofPayload;
  };

  const handleSaveProofPhotos = async () => {
    if (!isAssignedWorker) {
      toast.error('Only the primary assignee can upload completion proof.');
      return;
    }
    if (!onSaveTaskProof) {
      toast.error('Proof upload is not available right now.');
      return;
    }
    if (isUploading || proofDrafts.length === 0) return;

    setIsUploading(true);
    setUploadingProofDraftIds([]);

    try {
      const uploadedProof = await uploadProofDraftBatch();
      const mergedProof = [...proofPhotos, ...uploadedProof].slice(0, MAX_TASK_PROOF_PHOTOS);
      await withTimeout(Promise.resolve(onSaveTaskProof(mergedProof)), 30000);
      clearProofDrafts(proofDrafts);
      setProofDrafts([]);
      toast.success('Photos uploaded');
    } catch (error) {
      if (error instanceof Error && error.message === 'Operation timed out') {
        alert('Request timed out. Please check your connection.');
        return;
      }
      alert(error instanceof Error ? error.message : 'Failed to upload proof photos');
    } finally {
      setUploadingProofDraftIds([]);
      setIsUploading(false);
    }
  };

  const handleCompleteWithProofPhotos = async () => {
    if (!isAssignedWorker) {
      toast.error('Only the primary assignee can complete this task.');
      return;
    }
    if (!canCompleteTaskThisSession) {
      alert('Please add a remark in the chat before completing this task');
      return;
    }
    if (isUploading) return;
    if (totalProofCount === 0) {
      toast.error('Add at least 1 photo to complete this task');
      return;
    }

    setIsUploading(true);
    setUploadingProofDraftIds([]);

    try {
      const uploadedProof = await uploadProofDraftBatch();
      const mergedProof = [...proofPhotos, ...uploadedProof].slice(0, MAX_TASK_PROOF_PHOTOS);
      await withTimeout(Promise.resolve(onCompleteTask(mergedProof)), 30000);
      clearProofDrafts(proofDrafts);
      setProofDrafts([]);
    } catch (error) {
      if (error instanceof Error && error.message === 'Operation timed out') {
        alert('Request timed out. Please check your connection.');
        return;
      }
      alert(error instanceof Error ? error.message : 'Failed to upload proof photos');
    } finally {
      setUploadingProofDraftIds([]);
      setIsUploading(false);
    }
  };

  const syncMentionMenuFromInput = (value: string, caretPosition: number) => {
    const mentionContext = extractMentionContext(value, caretPosition);
    if (!mentionContext) {
      setMentionMenuOpen(false);
      setMentionStartIndex(null);
      setMentionQuery('');
      setMentionCandidates([]);
      return;
    }
    setMentionStartIndex(mentionContext.start);
    setMentionQuery(mentionContext.query);
    setMentionMenuOpen(true);
  };

  const applyMentionCandidate = (candidate: MentionCandidate) => {
    const textarea = remarkInputRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart ?? newRemark.length;
    const mentionStart = mentionStartIndex ?? Math.max(0, newRemark.slice(0, cursorPos).lastIndexOf('@'));
    if (mentionStart < 0) return;

    const before = newRemark.slice(0, mentionStart);
    const after = newRemark.slice(cursorPos);
    const mentionText = `@${candidate.name}`;
    const nextValue = `${before}${mentionText} ${after}`;
    const nextCaret = (before + mentionText + ' ').length;

    setNewRemark(nextValue);
    setSelectedMentions((prev) => {
      if (prev.some((entry) => entry.id === candidate.id)) return prev;
      return [...prev, candidate];
    });
    setMentionMenuOpen(false);
    setMentionCandidates([]);
    setMentionStartIndex(null);
    setMentionQuery('');
    setActiveMentionIndex(0);

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const extractMentionPayload = (text: string): { mentionedUserIds: string[]; mentionedDisplayNames: string[] } => {
    const normalizedText = text.toLowerCase();
    const mentioned = selectedMentions.filter((candidate) =>
      normalizedText.includes(`@${candidate.name.toLowerCase()}`)
    );
    return {
      mentionedUserIds: Array.from(new Set(mentioned.map((candidate) => candidate.id))),
      mentionedDisplayNames: Array.from(new Set(mentioned.map((candidate) => candidate.name))),
    };
  };

  const renderRemarkContent = (text: string, mentionNames?: string[]) => {
    const normalizedMentionNames = Array.from(
      new Set(
        (Array.isArray(mentionNames) && mentionNames.length > 0
          ? mentionNames
          : employees
              .map((employee) => String(employee.name || '').trim())
              .filter((name) => name && text.toLowerCase().includes(`@${name.toLowerCase()}`))
        )
          .map((name) => String(name || '').trim())
          .filter(Boolean)
      )
    );

    if (normalizedMentionNames.length === 0) {
      return text;
    }

    const pattern = normalizedMentionNames
      .map((name) => `@${escapeRegex(name)}`)
      .sort((a, b) => b.length - a.length)
      .join('|');

    if (!pattern) {
      return text;
    }

    const mentionRegex = new RegExp(`(${pattern})`, 'gi');
    return text.split(mentionRegex).map((part, index) => {
      if (!part) return null;
      if (part.startsWith('@')) {
        return (
          <button
            key={`${part}_${index}`}
            type="button"
            onClick={() => {
              const mentionName = part.slice(1).trim().toLowerCase();
              const profile = employees.find((employee) => String(employee.name || '').trim().toLowerCase() === mentionName);
              if (profile) {
                toast.info(`${profile.name}${profile.role ? ` · ${profile.role}` : ''}`);
              }
            }}
            className="rounded-md bg-indigo-100/80 px-1 font-bold text-indigo-700 transition hover:bg-indigo-200"
            title="Open profile"
          >
            {part}
          </button>
        );
      }
      return <React.Fragment key={`txt_${index}`}>{part}</React.Fragment>;
    });
  };

  const sendMentionNotifications = async (messageText: string) => {
    const payload = extractMentionPayload(messageText);
    const resolvedMentionIds = Array.from(
      new Set([
        ...payload.mentionedUserIds,
        ...payload.mentionedDisplayNames.flatMap((displayName) =>
          employees
            .filter((employee) => String(employee.name || '').trim().toLowerCase() === String(displayName || '').trim().toLowerCase())
            .map((employee) => employee.id)
        ),
      ])
    ).filter((userId) => userId && userId !== currentUser.id);

    if (resolvedMentionIds.length === 0) {
      return;
    }

    const actorName =
      currentUser.name?.trim() ||
      currentUser.email?.split('@')[0] ||
      'A teammate';
    const taskTitle = task.description?.trim() || 'Task';
    const taskCompanyId = String(task.company_id || currentUser.company_id || '').trim();
    const nowIso = new Date().toISOString();
    const mentionMessage = `${actorName} mentioned you in task: ${taskTitle}`;

    const notificationRows = resolvedMentionIds.map((userId) => ({
      user_id: userId,
      title: 'You were mentioned in a task message',
      body: mentionMessage,
      company_id: taskCompanyId || null,
      entity_type: 'task',
      entity_id: task.id,
      is_read: false,
      created_at: nowIso,
    }));

    const { error: mentionNotificationError } = await supabase
      .from('notifications')
      .insert(notificationRows);

    if (mentionNotificationError) {
      console.warn('Mention notification insert failed:', mentionNotificationError);
      logSupabaseQueryError(mentionNotificationError);
      return;
    }

    resolvedMentionIds.forEach((userId) => {
      void fetch(SEND_MENTION_PUSH_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mentioned_user_id: userId,
          sender_name: currentUser.name,
          task_description: task.description,
          task_id: task.id,
          company_id: taskCompanyId || currentUser.company_id,
        }),
      }).catch((pushError) => {
        console.warn('Mention push dispatch failed:', pushError);
      });
    });
  };

  const uploadChatMedia = async (draft: PendingChatMedia): Promise<Pick<TaskComment, 'media_url' | 'media_type' | 'media_name' | 'media_size'>> => {
    const storagePath = `${currentUser.company_id}/${task.id}/${Date.now()}_${sanitizeChatFileName(draft.name)}`;
    const startedAt = performance.now();

    const { error: uploadError } = await supabase.storage
      .from(CHAT_MEDIA_BUCKET)
      .upload(storagePath, draft.file, {
        contentType: draft.file.type || 'application/octet-stream',
        upsert: false,
      });

    if (uploadError) {
      throw new Error(uploadError.message || 'Failed to upload attachment.');
    }

    const { data } = supabase.storage
      .from(CHAT_MEDIA_BUCKET)
      .getPublicUrl(storagePath);

    const mediaPayload = {
      media_url: String(data?.publicUrl || '').trim(),
      media_type: draft.mediaType,
      media_name: draft.name,
      media_size: draft.size,
    };
    logTaskChatPerf('media uploaded', {
      taskId: task.id,
      bytes: draft.size,
      type: draft.mediaType,
      ms: Math.round(performance.now() - startedAt),
    });
    return mediaPayload;
  };

  const replaceOptimisticMessage = (optimisticId: string, updater: (existing: TaskCommentView) => TaskCommentView) => {
    setMessages((prev) =>
      prev.map((message) => {
        if (message.id !== optimisticId) {
          return message;
        }
        const next = updater(message);
        if (isBlobUrl(message.media_url) && message.media_url !== next.media_url) {
          URL.revokeObjectURL(String(message.media_url));
        }
        return next;
      })
    );
  };

  const handleAddRemark = async () => {
    const text = newRemark.trim();
    if (editingMessage) {
      if (!text && !editingMessage.media_url) {
        toast.error('Message cannot be empty');
        return;
      }

      const { error } = await supabase
        .from('task_comments')
        .update({
          message_text: text,
          is_edited: true,
          edited_at: new Date().toISOString(),
        })
        .eq('id', editingMessage.id);

      if (error) {
        toast.error(error.message || 'Failed to edit message');
        return;
      }

      setMessages((prev) =>
        prev.map((message) =>
          message.id === editingMessage.id
            ? {
                ...message,
                message_text: text,
                is_edited: true,
                edited_at: new Date().toISOString(),
              }
            : message
        )
      );
      setEditingMessage(null);
      setNewRemark('');
      return;
    }

    if (!text && !selectedChatMedia) {
      return;
    }

    let authenticatedUserId = String(currentUser.auth_user_id || currentUser.id || '').trim();
    if (!authenticatedUserId) {
      const { data: authData, error: authError } = await supabase.auth.getUser();
      authenticatedUserId = String(authData.user?.id || '').trim();
      if (authError || !authenticatedUserId) {
        toast.error(authError?.message || 'Unable to verify signed-in user');
        return;
      }
    }

    const optimisticId = `temp-${task.id}-${Date.now()}`;
    const optimisticCreatedAt = new Date().toISOString();
    const optimisticMessage: TaskCommentView = {
      id: optimisticId,
      task_id: task.id,
      sender_id: authenticatedUserId,
      message_text: text,
      created_at: optimisticCreatedAt,
      created_at_ms: parseTimestamp(optimisticCreatedAt) || Date.now(),
      sender_name: currentUser.name || 'You',
      sender_initials: getEmployeeInitials(currentUser.name || 'You'),
      reply_to_id: replyingToMessage?.id || null,
      reply_to: replyingToMessage
        ? {
            id: replyingToMessage.id,
            sender_name: replyingToMessage.sender_name,
            message_text: replyingToMessage.message_text,
            media_type: replyingToMessage.media_type || null,
            media_name: replyingToMessage.media_name || null,
          }
        : null,
      media_url: selectedChatMedia?.previewUrl || null,
      media_type: selectedChatMedia?.mediaType || null,
      media_name: selectedChatMedia?.name || null,
      media_size: selectedChatMedia?.size || null,
      is_edited: false,
      edited_at: null,
      optimistic: true,
      upload_progress: selectedChatMedia ? 8 : undefined,
    };

    pendingOwnMessageScrollRef.current = true;
    isNearChatBottomRef.current = true;
    setShowScrollBtn(false);
    setMessages((prev) => [...prev, optimisticMessage]);
    setRemarkAddedThisSession(true);
    setMentionMenuOpen(false);
    setMentionCandidates([]);
    setMentionStartIndex(null);
    setMentionQuery('');
    setActiveMentionIndex(0);
    setSelectedMentions([]);
    setNewRemark('');
    setReplyingToMessage(null);
    setShowAttachmentMenu(false);
    setMessageActionMenu(null);

    const mediaDraft = selectedChatMedia;
    setSelectedChatMedia(null);

    let uploadProgressTimer: number | null = null;
    try {
      if (mediaDraft) {
        uploadProgressTimer = window.setInterval(() => {
          replaceOptimisticMessage(optimisticId, (existing) => ({
            ...existing,
            upload_progress: Math.min(88, (existing.upload_progress || 0) + 18),
          }));
        }, 700);
      }

      const mediaPayload = mediaDraft ? await uploadChatMedia(mediaDraft) : {};

      const insertPayload = {
        task_id: task.id,
        sender_id: authenticatedUserId,
        message_text: text,
        reply_to_id: replyingToMessage?.id || null,
        ...mediaPayload,
      };

      const { data, error } = await supabase
        .from('task_comments')
        .insert(insertPayload)
        .select(TASK_COMMENT_COLUMNS)
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to send message');
      }

      replaceOptimisticMessage(optimisticId, () => {
        const normalized = normalizeTaskComments([data as TaskComment])[0];
        return {
          ...normalized,
          reply_to: optimisticMessage.reply_to || normalized.reply_to || null,
        };
      });

      if (text) {
        await sendMentionNotifications(text);
      }
    } catch (error) {
      setMessages((prev) => {
        const messageToRemove = prev.find((message) => message.id === optimisticId);
        if (messageToRemove?.media_url && isBlobUrl(messageToRemove.media_url)) {
          URL.revokeObjectURL(messageToRemove.media_url);
        }
        return prev.filter((message) => message.id !== optimisticId);
      });
      toast.error(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      if (uploadProgressTimer) {
        window.clearInterval(uploadProgressTimer);
      }
      if (mediaDraft?.previewUrl) {
        URL.revokeObjectURL(mediaDraft.previewUrl);
      }
    }
  };

  const updateTaskExtensionFields = async (snakePayload: Record<string, unknown>, camelPayload?: Record<string, unknown>) => {
    let { error } = await supabase.from('tasks').update(snakePayload).eq('id', task.id);
    if (!error) return null;
    const msg = String(error?.message || '').toLowerCase();
    if ((msg.includes('column') && msg.includes('does not exist')) && camelPayload) {
      const retry = await supabase.from('tasks').update(camelPayload).eq('id', task.id);
      error = retry.error;
    }
    return error || null;
  };

  const handleRequestExtension = async () => {
    if (!extensionDate || isExtensionUpdating) return;
    const requestedTs = new Date(extensionDate).getTime();
    if (!Number.isFinite(requestedTs)) { alert('Please select a valid date.'); return; }
    const requestedDueDateIso = toIsoStringFromMs(requestedTs);
    if (!requestedDueDateIso) { alert('Please select a valid date.'); return; }
    const companyId = String(task.company_id || currentUser.company_id || '').trim();
    const requesterEmployeeId = await resolveCurrentEmployeeIdForApproval(currentUser, companyId);
    const approverId = await resolveEmployeeIdForApproval(extensionApproverId, companyId);
    if (!requesterEmployeeId) { alert('Your employee profile could not be resolved for this extension request.'); return; }
    if (!approverId) { alert('Task creator could not be resolved for this extension request.'); return; }
    setIsExtensionUpdating(true);
    const error = await updateTaskExtensionFields(
      { extension_status: 'REQUESTED', requested_due_date: requestedDueDateIso },
      { extension_status: 'REQUESTED', requestedDueDate: requestedDueDateIso }
    );
    if (error) {
      setIsExtensionUpdating(false);
      alert(`Failed: ${error.message}`);
      return;
    }

    const approvalTitle = `Deadline Extension Request: ${task.description}`;
    const approvalDescription = `Requested new deadline: ${new Date(requestedTs).toLocaleString()}`;
    const { data: createdApproval, error: approvalError } = await supabase
      .from('approvals')
      .insert({
        requester_id: requesterEmployeeId,
        approver_id: approverId,
        title: approvalTitle,
        description: approvalDescription,
        amount: null,
        status: 'PENDING',
        task_id: task.id,
        company_id: companyId || null,
      })
      .select('id')
      .maybeSingle();

    setIsExtensionUpdating(false);
    if (approvalError) {
      console.warn('Failed to create extension approval request:', approvalError);
      logSupabaseQueryError(approvalError);
    }
    setShowExtensionInput(false);
  };

  const handleExtensionDecision = async (decision: 'APPROVED' | 'REJECTED') => {
    if (isExtensionUpdating || !requestedDueDate) return;
    setIsExtensionUpdating(true);
    const snake: Record<string, unknown> = { extension_status: decision };
    const camel: Record<string, unknown> = { extension_status: decision };
    if (decision === 'APPROVED') { snake.deadline = requestedDueDate; camel.deadline = requestedDueDate; }
    const error = await updateTaskExtensionFields(snake, camel);
    setIsExtensionUpdating(false);
    if (error) alert(`Failed: ${error.message}`);
  };

  const handleCompleteWithoutPhoto = async () => {
    if (!isAssignedWorker) {
      toast.error('Only the primary assignee can complete this task.');
      return;
    }
    if (!canCompleteTaskThisSession) {
      alert('Please add a remark in the chat before completing this task');
      return;
    }
    if (isCompleting) return;
    setIsCompleting(true);
    try {
      await withTimeout(Promise.resolve(onCompleteTaskWithoutPhoto()), 30000);
    } catch (error) {
      if (error instanceof Error && error.message === 'Operation timed out') {
        alert('Request timed out. Please check your connection.');
        return;
      }
      alert(`Failed to complete task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsCompleting(false);
    }
  };

  const handleReopenTask = async () => {
    if (isReopening) return;
    setIsReopening(true);
    try {
      await withTimeout(Promise.resolve(onReopenTask()), 30000);
    } catch (error) {
      if (error instanceof Error && error.message === 'Operation timed out') {
        alert('Request timed out. Please check your connection.');
        return;
      }
      alert(`Failed to reopen task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsReopening(false);
    }
  };

  const handleEditSave = async () => {
    if (!onInlineEditSave || isSavingEdit) return;
    const rec: RecurrenceFrequency | null = editTaskType === 'recurring' ? (editRecurrenceFrequency || null) : null;
    const recurrenceTimeValue =
      editTaskType === 'recurring' && rec
        ? (normalizeRecurrenceTime(editRecurrenceTime) || getRoundedFiveMinuteTime())
        : null;
    const rawDeadline = editDeadline ? new Date(editDeadline).getTime() : undefined;
    const safeDeadline = typeof rawDeadline === 'number' && Number.isFinite(rawDeadline) ? rawDeadline : undefined;
    if (!editDescription.trim()) { alert('Description required.'); return; }
    if (editTaskType === 'recurring' && !rec) { alert('Select recurrence frequency.'); return; }
    setIsSavingEdit(true);
    const ok = await onInlineEditSave(task.id, {
      description: editDescription.trim(),
      assignedTo: editAssigneeId === 'none' ? null : editAssigneeId,
      deadline: safeDeadline,
      requirePhoto: editRequirePhoto,
      taskType: editTaskType,
      recurrenceFrequency: rec,
      recurrenceTime: recurrenceTimeValue,
      priority: editIsHighPriority ? 'High' : 'Medium',
    });
    setIsSavingEdit(false);
    if (ok) setIsEditing(false);
  };

  const handleDeleteAndGoBack = async () => {
    if (!window.confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      return;
    }
    if (isDeleting) return;
    setIsDeleting(true);
    try {
      await withTimeout(Promise.resolve(onDelete()), 30000);
      onBack();
    } catch (error) {
      if (error instanceof Error && error.message === 'Operation timed out') {
        alert('Request timed out. Please check your connection.');
        return;
      }
      alert(`Failed to delete task: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStopRecurrence = async () => {
    if (isStoppingRecurrence) return;
    const confirmStop = window.confirm(
      'Are you sure you want to stop this recurring task? It will no longer repeat after the current cycle.'
    );
    if (!confirmStop) return;

    setIsStoppingRecurrence(true);
    try {
      if (onInlineEditSave) {
        const ok = await onInlineEditSave(task.id, {
          description: String(task.description || ''),
          assignedTo: task.assignedTo || null,
          deadline: task.deadline ?? undefined,
          requirePhoto: Boolean(task.requirePhoto),
          taskType: 'one_time',
          recurrenceFrequency: null,
          recurrenceTime: null,
          priority: normalizedPriority,
        });
        if (!ok) {
          toast.error('Failed to stop recurrence');
          return;
        }
      } else {
        const { error } = await supabase
          .from('tasks')
          .update({
            task_type: 'one_time',
            recurrence_frequency: null,
            recurrence_time: null,
            next_recurrence_notification_at: null,
          })
          .eq('id', task.id);
        if (error) {
          toast.error(`Failed to stop recurrence: ${error.message}`);
          return;
        }
        if (typeof window !== 'undefined') {
          window.location.reload();
        }
      }
      toast.success('Recurrence stopped');
    } finally {
      setIsStoppingRecurrence(false);
    }
  };

  // Auto-start task when opened and status is pending
  useEffect(() => {
    if (!readOnly && isAssignedWorker && task.status === 'pending' && onStartTask) {
      onStartTask();
    }
  }, [task.id, task.status, onStartTask, readOnly, isAssignedWorker]); // Only run when task ID changes (component mounts with new task)

  // --- Build Quick Actions ---
  type QuickAction = {
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    className: string;
    useLoadingButton?: boolean;
    isLoading?: boolean;
    loadingText?: string;
    disabled?: boolean;
  };
	  const actions: QuickAction[] = [];
	  const quickActionButtonClass = '!h-20 !w-full !rounded-2xl border border-transparent !px-2 !py-3 !text-sm !font-bold shadow-[0_8px_20px_rgba(15,23,42,0.08)]';
  const canStopRecurrence = isManager && normalizedTaskType === 'recurring';

  if (task.status === 'pending') {
    // Start Task button removed - task auto-starts when opened
    if (canDelegateTask) {
      actions.push({
        key: 'delegate',
        label: 'Add Collaborator',
        icon: <GitFork className="w-5 h-5" />,
        onClick: onDelegate,
        className: '!bg-[var(--accent)] !text-white hover:!bg-[#4338CA]'
      });
    }
    if (canEdit) {
      actions.push({
        key: 'edit',
        label: 'Edit',
        icon: <Edit className="w-5 h-5" />,
        onClick: () => setIsEditing(true),
        className: '!bg-[var(--accent-light)] !text-[var(--accent)] hover:!bg-[var(--accent-light)] border border-[var(--accent)]/20'
      });
    }
    if (canDelete) {
      actions.push({
        key: 'delete',
        label: 'Delete',
        icon: <Trash2 className="w-5 h-5" />,
        onClick: handleDeleteAndGoBack,
        useLoadingButton: true,
        isLoading: isDeleting,
        loadingText: 'Deleting...',
        disabled: isDeleting,
        className: '!bg-[#EF4444] !text-white hover:!bg-[#DC2626]'
      });
    }
  }

  if (task.status === 'in-progress') {
    if (canDelegateTask) {
      actions.push({
        key: 'delegate',
        label: 'Add Collaborator',
        icon: <GitFork className="w-5 h-5" />,
        onClick: onDelegate,
        className: '!bg-[var(--accent)] !text-white hover:!bg-[#4338CA]'
      });
    }
    if (isAssignedWorker && task.requirePhoto) {
      actions.push({
        key: 'complete-photo',
        label:
          proofDrafts.length >= MAX_TASK_PROOF_PHOTOS
            ? '5/5 Added'
            : proofDrafts.length > 0
            ? 'Add More'
            : 'Add Photos',
        icon: <Camera className="w-5 h-5" />,
        onClick: openProofPicker,
        disabled: isUploading || proofDrafts.length >= MAX_TASK_PROOF_PHOTOS,
        className: '!bg-[#10B981] !text-white hover:!bg-[#059669]'
      });
		    } else if (isAssignedWorker) {
		      actions.push({
		        key: 'complete',
		        label: 'Complete',
	        icon: <Check className="w-5 h-5" />,
		        onClick: handleCompleteWithoutPhoto,
		        useLoadingButton: true,
		        isLoading: isCompleting,
		        loadingText: 'Completing...',
		        disabled: isCompleting || !canPrimaryAssigneeCompleteThisSession,
		        className:
		          canPrimaryAssigneeCompleteThisSession
		            ? '!bg-[#10B981] !text-white hover:!bg-[#059669]'
		            : '!bg-slate-200 !text-slate-500 hover:!bg-slate-200'
		      });
	    }
    if (canRequestExtension) {
      actions.push({
        key: 'extension',
        label: 'Extension',
        icon: <Clock className="w-5 h-5" />,
        onClick: () => setShowExtensionInput(prev => !prev),
        className: '!bg-[var(--orange-light)] !text-[var(--orange)] hover:!bg-amber-100 border border-amber-200'
      });
    }
    if (canEdit) {
      actions.push({
        key: 'edit',
        label: 'Edit',
        icon: <Edit className="w-5 h-5" />,
        onClick: () => setIsEditing(true),
        className: '!bg-[var(--accent-light)] !text-[var(--accent)] hover:!bg-[var(--accent-light)] border border-[var(--accent)]/20'
      });
    }
    if (canDelete) {
      actions.push({
        key: 'delete',
        label: 'Delete',
        icon: <Trash2 className="w-5 h-5" />,
        onClick: handleDeleteAndGoBack,
        useLoadingButton: true,
        isLoading: isDeleting,
        loadingText: 'Deleting...',
        disabled: isDeleting,
        className: '!bg-[#EF4444] !text-white hover:!bg-[#DC2626]'
      });
    }
  }

  if (task.status === 'completed') {
    if (proofPhotos.length > 0) {
      actions.push({
        key: 'view-proof',
        label: 'View Proof',
        icon: <Eye className="w-5 h-5" />,
        onClick: () => setSelectedProofIndex(0),
        className: '!bg-[var(--accent-light)] !text-[var(--accent)] hover:!bg-[var(--accent-light)] border border-[var(--accent)]/20'
      });
    }
    if (isManager) {
      actions.push({
        key: 'reopen',
        label: 'Reopen',
        icon: <RotateCcw className="w-5 h-5" />,
        onClick: handleReopenTask,
        useLoadingButton: true,
        isLoading: isReopening,
        loadingText: 'Reopening...',
        disabled: isReopening,
        className: '!bg-[var(--accent)] !text-white hover:!bg-[#4338CA]'
      });
    }
    if (canDelete) {
      actions.push({
        key: 'delete',
        label: 'Delete',
        icon: <Trash2 className="w-5 h-5" />,
        onClick: handleDeleteAndGoBack,
        useLoadingButton: true,
        isLoading: isDeleting,
        loadingText: 'Deleting...',
        disabled: isDeleting,
        className: '!bg-[#EF4444] !text-white hover:!bg-[#DC2626]'
      });
    }
  }

	  if (task.status === 'pending_approval' && proofPhotos.length > 0) {
    actions.push({
	      key: 'view-proof',
	      label: 'View Proof',
	      icon: <Eye className="w-5 h-5" />,
	      onClick: () => setSelectedProofIndex(0),
	      className: '!bg-[var(--accent-light)] !text-[var(--accent)] hover:!bg-[var(--accent-light)] border border-[var(--accent)]/20'
	    });
	  }

  if (canStopRecurrence) {
    actions.push({
      key: 'stop-recurrence',
      label: 'Stop Recurrence',
      icon: <Clock className="w-5 h-5" />,
      onClick: handleStopRecurrence,
      useLoadingButton: true,
      isLoading: isStoppingRecurrence,
      loadingText: 'Stopping...',
      disabled: isStoppingRecurrence,
      className: '!bg-amber-500 !text-white hover:!bg-amber-600'
    });
  }

  // --- Status label ---
  const statusLabel =
    task.status === 'completed'
      ? 'Completed'
      : task.status === 'pending_approval'
      ? 'Pending Approval'
      : task.status === 'in-progress'
      ? 'In Progress'
      : 'Pending';
  const statusColor =
    task.status === 'completed'
      ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
      : task.status === 'pending_approval'
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : task.status === 'in-progress'
      ? 'text-[var(--accent)] bg-[var(--accent-light)] border-[var(--accent)]/20'
      : isOverdue
      ? 'text-red-600 bg-red-50 border-red-200'
      : 'text-slate-600 bg-slate-100 border-slate-200';
  const heroAccentClass =
    task.status === 'completed'
      ? 'border-l-[var(--green)] bg-[var(--green-light)]/55'
      : task.status === 'pending_approval'
      ? 'border-l-[var(--orange)] bg-[var(--orange-light)]/60'
      : isOverdue
      ? 'border-l-[var(--red)] bg-[var(--red-light)]/60'
      : 'border-l-[var(--accent)] bg-white';
  const mobileEditOverlayStyle =
    editModalVisibleHeight && typeof window !== 'undefined' && window.innerWidth < 640
      ? {
          paddingTop: '16px',
          paddingBottom: `${editModalKeyboardInset + 16}px`,
        }
      : undefined;
  const mobileEditModalStyle =
    editModalVisibleHeight && typeof window !== 'undefined' && window.innerWidth < 640
      ? {
          maxHeight: `${Math.max(editModalVisibleHeight - 32, 320)}px`,
        }
      : undefined;
  const mobileEditModalBodyStyle =
    editModalKeyboardInset > 0
      ? {
          paddingBottom: `${editModalKeyboardInset + 24}px`,
          scrollPaddingBottom: `${editModalKeyboardInset + 120}px`,
          overscrollBehavior: 'contain' as const,
        }
      : undefined;

  return (
    <div className={`fixed inset-x-0 top-0 z-30 flex items-end justify-center bg-slate-900/40 transition-[bottom] duration-200 ease-out md:inset-0 md:z-[80] md:items-center md:p-4 ${isTaskKeyboardActive ? 'bottom-0' : 'bottom-24'}`}>
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-[var(--surface)] pt-[env(safe-area-inset-top)] md:h-auto md:max-h-[90vh] md:max-w-2xl md:rounded-2xl md:pt-0 md:shadow-xl">
        {/* ─── Header ─── */}
        <div className="sticky top-0 z-20 flex max-h-[110px] shrink-0 flex-col gap-1 border-b border-slate-200/80 bg-white/95 px-3 py-2 shadow-[0_6px_20px_rgba(15,23,42,0.05)] backdrop-blur md:px-4">
          {/* TaskRoomHeader */}
          <div className="flex min-h-0 items-center gap-2">
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-700 shadow-sm transition-colors hover:bg-slate-100 active:scale-95"
              aria-label="Back to tasks"
            >
              <ArrowLeft className="h-[18px] w-[18px] text-slate-700" />
            </button>
            <h1 className="relative min-w-0 flex-1 line-clamp-2 pr-4 text-[14px] font-semibold leading-[1.22] text-slate-950 after:pointer-events-none after:absolute after:right-0 after:top-0 after:h-full after:w-8 after:bg-gradient-to-l after:from-white after:to-transparent md:text-[15px]">
              {task.description}
            </h1>
            <button
              type="button"
              onClick={() => setActiveTab('details')}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 shadow-sm transition-colors hover:bg-slate-100"
              aria-label="Open task details"
              title="Open details"
            >
              <Eye className="h-[18px] w-[18px]" />
            </button>
          </div>
          {/* TaskMetaStrip */}
          <div className="flex min-h-[26px] items-center gap-1.5 overflow-x-auto pl-11">
            <span className={`font-ui-mono inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] ${
              normalizedPriority === 'High' ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'
            }`}>
              {normalizedPriority === 'High' && <Flag className="h-3 w-3 fill-current" />}
              {normalizedPriority}
            </span>
            {isOverdue && (
              <span className="font-ui-mono inline-flex shrink-0 rounded-full bg-red-50 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] text-red-600">
                {overdueDays}d overdue
              </span>
            )}
            <span className={`font-ui-mono inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${statusColor}`}>
              {statusLabel}
            </span>
            <div className="ml-1 flex shrink-0 -space-x-1.5">
              {[task.assignedTo, task.assignedBy, ...collaboratorIds].filter(Boolean).slice(0, 4).map((id) => (
                <span key={String(id)} className="relative flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-slate-200 text-[8px] font-extrabold text-slate-700">
                  {getEmployeeInitials(getEmployeeName(String(id)))}
                  <span className="absolute bottom-0 right-0 h-1.5 w-1.5 rounded-full border border-white bg-emerald-400" />
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Scrollable Content ─── */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-slate-50/80">
        {/* TaskTabs */}
        <div className="sticky top-0 z-10 border-b border-slate-200/70 bg-white/95 px-3 backdrop-blur md:px-5">
          <div className="mx-auto grid h-10 max-w-3xl grid-cols-2">
            {(['chat', 'details'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`relative px-3 py-2 text-[13px] font-semibold capitalize transition-colors ${
                  activeTab === tab ? 'text-slate-950' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {activeTab === tab && (
                  <motion.span
                    layoutId="taskDetailsActiveTab"
                    className="absolute inset-x-6 bottom-0 h-0.5 rounded-full bg-slate-950"
                    transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  />
                )}
                <span className="relative">{tab}</span>
              </button>
            ))}
          </div>
        </div>
        <AnimatePresence mode="wait" initial={false}>
        {activeTab === 'details' && (
          <motion.div
            key="details"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
	            transition={{ duration: 0.18 }}
              className="mx-auto min-h-0 w-full max-w-3xl flex-1 space-y-3 overflow-y-auto px-3 py-3 md:px-5"
	          >
        {/* ── Section: Task Info ── */}
	        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className={`rounded-2xl border border-[var(--border)] border-l-4 px-4 py-4 shadow-[0_1px_3px_rgba(0,0,0,0.06)] ${heroAccentClass}`}>
            <div className="flex items-start justify-between gap-3">
              <span className={`font-ui-mono inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.16em] ${
                normalizedPriority === 'High' ? 'border-red-200 bg-white text-[var(--red)]' : 'border-[var(--accent)]/20 bg-white text-[var(--accent)]'
              }`}>
                {normalizedPriority === 'High' && <Flag className="w-3.5 h-3.5 fill-current" />}
                {normalizedPriority} Priority
              </span>
              {isOverdue && (
                <span className="font-ui-mono rounded-full border border-red-200 bg-[var(--red-light)] px-2.5 py-1 text-[11px] font-medium text-[var(--red)]">
                  {overdueDays} day{overdueDays === 1 ? '' : 's'} overdue
                </span>
              )}
            </div>
            <h2 className="mt-3 text-[23px] font-extrabold text-[var(--ink)] leading-snug break-words">
              {task.description}
            </h2>
          </div>

          <div className="mt-4 space-y-0">
            {/* Assigned to */}
            <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)] text-[var(--ink-3)]"><User className="w-4 h-4 flex-shrink-0" /></span>
              <span className="w-[90px] text-sm text-[var(--ink-3)]">Assigned to</span>
              <span className="text-sm font-semibold text-slate-900 ml-1 inline-flex items-center gap-2">
                <span>{assigneeName}</span>
                {assigneeTelHref && (
                  <a
                    href={assigneeTelHref}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors border border-emerald-200"
                    title={`Call ${assigneeName}`}
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                )}
              </span>
            </div>

            {/* Assigned by */}
            {task.assignedBy && (
              <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)] text-[var(--ink-3)]"><UserPlus className="w-4 h-4 flex-shrink-0" /></span>
                <span className="w-[90px] text-sm text-[var(--ink-3)]">Assigned by</span>
                <span className="text-sm font-semibold text-slate-900 ml-1 inline-flex items-center gap-2">
                  <span>{assignerName}</span>
                  {assignerTelHref && (
                    <a
                      href={assignerTelHref}
                      className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors border border-emerald-200"
                      title={`Call ${assignerName}`}
                    >
                      <Phone className="w-4 h-4" />
                    </a>
                  )}
                </span>
              </div>
            )}

            {collaboratorSummaries.length > 0 && (
              <div className="flex items-start gap-3 py-3 border-b border-[var(--border)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)] text-[var(--ink-3)]"><GitFork className="w-4 h-4 flex-shrink-0" /></span>
                <span className="w-[90px] pt-1 text-sm text-[var(--ink-3)]">Collaborators</span>
                <div className="ml-1 flex flex-wrap gap-2">
                  {collaboratorSummaries.map((collaborator) => (
                    <span
                      key={collaborator.id}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700"
                    >
                      <span>{collaborator.name}</span>
                      {collaborator.telHref && (
                        <a
                          href={collaborator.telHref}
                          className="inline-flex items-center justify-center rounded-full bg-white p-1 text-emerald-700 transition-colors hover:bg-emerald-50"
                          title={`Call ${collaborator.name}`}
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Due date */}
            <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><Calendar className={`w-4 h-4 flex-shrink-0 ${isOverdue ? 'text-red-400' : 'text-slate-400'}`} /></span>
              <span className="w-[90px] text-sm text-[var(--ink-3)]">Due date</span>
              <span className={`font-ui-mono text-sm font-medium ml-1 ${isOverdue ? 'text-red-600' : 'text-emerald-600'}`}>
                {task.deadline ? formatDateTimeWithMeridiem(task.deadline) : 'No deadline'}
              </span>
            </div>

            {/* Created */}
            <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><Clock className="w-4 h-4 text-slate-400 flex-shrink-0" /></span>
              <span className="w-[90px] text-sm text-[var(--ink-3)]">Created</span>
              <span className="font-ui-mono text-sm font-medium text-slate-700 ml-1">
                {formatDateTimeWithMeridiem(Number(task.createdAt || 0))}
              </span>
            </div>

            {/* Requested date (extension) */}
            {extensionStatus === 'REQUESTED' && requestedDueDate && (
              <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><CalendarClock className="w-4 h-4 text-amber-500 flex-shrink-0" /></span>
                <span className="w-[90px] text-sm text-[var(--ink-3)]">Requested</span>
                <span className="font-ui-mono text-sm font-medium text-amber-600 ml-1">{formatFullDate(requestedDueDate)}</span>
              </div>
            )}

            {/* Status */}
            <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><CheckCircle className={`w-4 h-4 flex-shrink-0 ${task.status === 'completed' ? 'text-emerald-500' : 'text-slate-400'}`} /></span>
              <span className="w-[90px] text-sm text-[var(--ink-3)]">Status</span>
              <span className={`font-ui-mono text-xs font-medium uppercase px-2.5 py-0.5 rounded-full border ml-1 ${statusColor}`}>{statusLabel}</span>
            </div>

            {isPendingApproval && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                Task completion is waiting for manager approval. It will move to completed only after the linked approval request is approved.
              </div>
            )}

            {resolvedParentTask && (resolvedParentTask.description || resolvedParentTask.title) && (
              <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><Layers className="w-4 h-4 text-amber-400 flex-shrink-0" /></span>
                <span className="w-[90px] text-sm text-[var(--ink-3)]">Part of</span>
                <span className="text-sm font-semibold text-amber-700 ml-1">
                  {resolvedParentTask.description || resolvedParentTask.title}
                </span>
              </div>
            )}

            {/* Recurrence badge */}
            {normalizedTaskType === 'recurring' && (
              <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><Clock className="w-4 h-4 text-indigo-400 flex-shrink-0" /></span>
                <span className="w-[90px] text-sm text-[var(--ink-3)]">Recurrence</span>
                <span className="font-ui-mono text-xs font-medium uppercase px-2.5 py-0.5 rounded-full bg-[var(--accent-light)] text-[var(--accent)] border border-[var(--accent)]/20 ml-1">
                  {normalizedRecurrence ? normalizedRecurrence.charAt(0).toUpperCase() + normalizedRecurrence.slice(1) : 'Recurring'}
                </span>
              </div>
            )}
            {normalizedTaskType === 'recurring' && (
              <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><Clock className="w-4 h-4 text-indigo-400 flex-shrink-0" /></span>
                <span className="w-[90px] text-sm text-[var(--ink-3)]">Resurfaces at</span>
                <span className="font-ui-mono text-sm font-medium text-slate-900 ml-1">
                  {formatRecurrenceTimeLabel(recurrenceTime)}
                </span>
              </div>
            )}

            {/* Photo required */}
            {task.requirePhoto && (
              <div className="flex items-center gap-3 py-3 border-b border-[var(--border)]">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><Camera className="w-4 h-4 text-slate-400 flex-shrink-0" /></span>
                <span className="w-[90px] text-sm text-[var(--ink-3)]">Proof</span>
                <span className="font-ui-mono text-xs font-medium uppercase px-2.5 py-0.5 rounded-full bg-slate-800 text-white">Photo Required</span>
              </div>
            )}

            {/* Completed timestamp */}
            {task.status === 'completed' && task.completedAt && (
              <div className="flex items-center gap-3 py-3">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] bg-[var(--surface-2)]"><Check className="w-4 h-4 text-emerald-500 flex-shrink-0" /></span>
                <span className="w-[90px] text-sm text-[var(--ink-3)]">Completed</span>
                <span className="font-ui-mono text-sm font-medium text-emerald-600 ml-1">{formatFullDate(task.completedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Section: Quick Actions Grid ── */}
        {proofPhotos.length > 0 && (
          <div className="px-4 md:px-6 py-4 border-b border-[var(--border)]">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="section-kicker">Proof Photos</h3>
              <span className="font-ui-mono text-[11px] font-medium uppercase tracking-[0.2em] text-slate-500">
                {proofPhotos.length} photo{proofPhotos.length === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {proofPhotos.map((photo, index) => (
                <button
                  key={`${photo.imageUrl}-${photo.timestamp}-${index}`}
                  type="button"
                  onClick={() => setSelectedProofIndex(index)}
                  className="group overflow-hidden rounded-2xl border border-[var(--border)] bg-white text-left transition-transform active:scale-[0.98]"
                >
                  <div className="aspect-square overflow-hidden bg-slate-100">
                    <img
                      src={photo.imageUrl}
                      alt={`Task proof ${index + 1}`}
                      className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                    />
                  </div>
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold text-slate-900">Photo {index + 1}</p>
                    <p className="text-xs text-slate-500">{new Date(photo.timestamp).toLocaleString()}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!readOnly && isAssignedWorker && task.status === 'in-progress' && task.requirePhoto && (
          <div className="px-4 md:px-6 py-4 border-b border-[var(--border)]">
            <h3 className="section-kicker mb-3">Completion Proof</h3>
            <div className="rounded-[24px] border border-[var(--border)] bg-white p-4 sm:p-5 space-y-4">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Add 1 to 5 photos before completing this task.</p>
                  <p className="text-xs text-slate-500">Each photo uploads separately to the `task-proofs` bucket.</p>
                </div>
                <span className="inline-flex w-fit items-center rounded-full bg-[var(--surface-2)] px-3 py-1 font-ui-mono text-[11px] font-medium uppercase tracking-[0.2em] text-slate-600">
                  {totalProofCount}/{MAX_TASK_PROOF_PHOTOS} photos added
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {proofDrafts.map((draft, index) => {
                  const isDraftUploading = uploadingProofDraftIds.includes(draft.id);
                  return (
                    <div
                      key={draft.id}
                      className="relative overflow-hidden rounded-2xl border border-[var(--border)] bg-slate-100"
                    >
                      <img
                        src={draft.previewUrl}
                        alt={`Selected proof ${index + 1}`}
                        className="aspect-square h-full w-full object-cover"
                      />
                      <div className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/65 px-2 py-1 text-[11px] font-medium text-white">
                        Photo {index + 1}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeProofDraft(draft.id)}
                        disabled={isDraftUploading || isUploading}
                        className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-700 shadow-sm transition hover:bg-white disabled:opacity-50"
                        aria-label={`Remove photo ${index + 1}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                      {isDraftUploading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900/65 text-white">
                          <Loader2 className="h-6 w-6 animate-spin" />
                          <span className="text-xs font-medium">Uploading...</span>
                        </div>
                      )}
                    </div>
                  );
                })}

                {totalProofCount < MAX_TASK_PROOF_PHOTOS && (
                  <button
                    type="button"
                    onClick={openProofPicker}
                    disabled={isUploading}
                    className="flex aspect-square flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[var(--border)] bg-[var(--surface-2)] px-4 text-center text-slate-600 transition hover:border-[var(--accent)] hover:text-[var(--accent)] disabled:opacity-50"
                  >
                    <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-[var(--accent)] shadow-sm">
                      <Plus className="h-6 w-6" />
                    </span>
                    <span className="text-sm font-semibold">Add Photo</span>
                    <span className="text-xs text-slate-500">Camera or gallery</span>
                  </button>
                )}
              </div>

              <input
                ref={proofInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleProofSelection}
                className="hidden"
              />

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1 text-xs text-slate-500">
                  <p>Minimum 1 photo required.</p>
                  {totalProofCount >= MAX_TASK_PROOF_PHOTOS && (
                    <p className="font-medium text-amber-600">Maximum 5 photos reached.</p>
                  )}
                  {requiresFreshCompletionRemark && !remarkAddedThisSession && (
                    <p className="font-medium text-amber-600">Add a remark in the chat below before completing.</p>
                  )}
                </div>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={openProofPicker}
                    disabled={isUploading || totalProofCount >= MAX_TASK_PROOF_PHOTOS}
                    className="rounded-xl border border-[var(--border)] bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Add Photo
                  </button>
                  <LoadingButton
                    type="button"
                    onClick={handleSaveProofPhotos}
                    isLoading={isUploading}
                    loadingText="Uploading..."
                    variant="secondary"
                    disabled={proofDrafts.length === 0 || isUploading}
                    className="rounded-xl px-4 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Upload Now
                  </LoadingButton>
                  <LoadingButton
                    type="button"
                    onClick={handleCompleteWithProofPhotos}
                    isLoading={isUploading}
                    loadingText="Uploading..."
                    variant="primary"
                    disabled={totalProofCount === 0 || isUploading || !canPrimaryAssigneeCompleteThisSession}
                    className={`rounded-xl px-4 py-3 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60 ${
                      canPrimaryAssigneeCompleteThisSession
                        ? 'bg-[#10B981] text-white hover:bg-[#059669]'
                        : 'bg-slate-200 text-slate-500 hover:bg-slate-200'
                    }`}
                  >
                    Complete Task
                  </LoadingButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {!readOnly && isAssignedWorker && task.status === 'in-progress' && !task.requirePhoto && requiresFreshCompletionRemark && !remarkAddedThisSession && (
          <div className="px-4 md:px-6 py-4 border-b border-[var(--border)]">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-700">
              Add a remark in the chat below before completing.
            </div>
          </div>
        )}

        {!readOnly && actions.length > 0 && (
          <div className="px-4 md:px-6 py-4 border-b border-[var(--border)]">
            <h3 className="section-kicker mb-3">Quick Actions</h3>
	            <div className={`grid gap-3 ${actions.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
	              {actions.map((action) =>
	                action.useLoadingButton ? (
		                  <LoadingButton
	                    key={action.key}
	                    type="button"
                    onClick={action.onClick}
                    isLoading={Boolean(action.isLoading)}
                    loadingText={action.loadingText}
                    disabled={action.disabled}
                    variant="primary"
		                    className={`flex flex-col items-center justify-center gap-1.5 transition-all active:scale-95 ${quickActionButtonClass} ${action.className}`}
		                  >
	                    {action.icon}
	                    <span className="text-sm font-bold">{action.label}</span>
	                  </LoadingButton>
	                ) : (
		                  <button
                    key={action.key}
                    type="button"
                    onClick={action.onClick}
                    disabled={action.disabled}
		                    className={`flex flex-col items-center justify-center gap-1.5 transition-all active:scale-95 disabled:opacity-50 ${quickActionButtonClass} ${action.className}`}
		                  >
	                    {action.icon}
	                    <span className="text-sm font-bold">{action.label}</span>
	                  </button>
	                )
	              )}
            </div>
          </div>
        )}

        {/* ── Section: Extension Request Input ── */}
        {!readOnly && canRequestExtension && showExtensionInput && (
          <div className="px-4 md:px-6 py-4 border-b border-slate-100">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
              <p className="text-sm font-bold text-amber-700">Request Deadline Extension</p>
              <div className="flex items-center gap-2">
                <div
                  className="relative flex-1 min-h-[52px] border border-amber-200 rounded-xl px-4 py-3.5 bg-white text-base text-slate-900 focus-within:ring-2 focus-within:ring-amber-300 cursor-pointer"
                  onClick={openExtensionPicker}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      openExtensionPicker();
                    }
                  }}
                >
                  <span className="block pr-10">
                    {extensionDate ? formatDateTimeLabel(extensionDate) : ''}
                  </span>
                  <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-amber-600 pointer-events-none" />
                  <input
                    ref={extensionInputRef}
                    type="datetime-local"
                    value={extensionDate || ''}
                    onChange={(e) => setExtensionDate(e.target.value || '')}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
                {extensionDate && (
                  <button
                    type="button"
                    onClick={() => setExtensionDate('')}
                    className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
                    aria-label="Clear date"
                  >
                    X Clear
                  </button>
                )}
              </div>
              <LoadingButton
                type="button"
                onClick={handleRequestExtension}
                isLoading={isExtensionUpdating}
                loadingText="Sending..."
                variant="secondary"
                disabled={!extensionDate || isExtensionUpdating}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-3 text-sm font-bold disabled:opacity-50 transition-colors"
                style={{ minHeight: 48 }}
              >
                Send Extension Request
              </LoadingButton>
            </div>
          </div>
        )}

        {/* Staff extension pending indicator */}
        {isAssignedWorker && extensionStatus === 'REQUESTED' && !canReviewExtensionRequest && (
          <div className="px-4 md:px-6 py-4 border-b border-slate-100">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
              <p className="text-sm font-bold text-amber-700">Extension Requested</p>
              <p className="text-xs text-amber-600 mt-1">Waiting for manager approval</p>
            </div>
          </div>
        )}

        {/* ── Section: Manager Approval ── */}
        {!readOnly && canReviewExtensionRequest && (
          <div className="px-4 md:px-6 py-4 border-b border-slate-100">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-3">
              <p className="text-base font-bold text-amber-700">Extension Requested</p>
              <p className="text-sm text-slate-600 mt-1">
                New deadline: {requestedDueDate ? formatFullDate(requestedDueDate) : 'selected date'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <LoadingButton
                type="button"
                onClick={() => handleExtensionDecision('APPROVED')}
                isLoading={isExtensionUpdating}
                loadingText="Updating..."
                variant="success"
                disabled={isExtensionUpdating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-3.5 text-base font-bold disabled:opacity-50 transition-colors active:scale-95"
                style={{ minHeight: 52 }}
              >
                Approve
              </LoadingButton>
              <LoadingButton
                type="button"
                onClick={() => handleExtensionDecision('REJECTED')}
                isLoading={isExtensionUpdating}
                loadingText="Updating..."
                variant="danger"
                disabled={isExtensionUpdating}
                className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-xl py-3.5 text-base font-bold disabled:opacity-50 transition-colors active:scale-95"
                style={{ minHeight: 52 }}
              >
                Reject
              </LoadingButton>
            </div>
          </div>
        )}

        {/* ── Section: Discussion ── */}
          </motion.div>
        )}

        {activeTab === 'chat' && (
          <motion.div
            key="chat"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
        {/* TaskChatView */}
        <div className="relative mx-auto flex min-h-0 w-full max-w-3xl flex-1 flex-col overflow-hidden px-2 pt-2 pb-1 md:px-5">
          {/* SystemEvent */}
          <div className="mb-1.5 flex justify-center">
            <span className="inline-flex items-center rounded-full bg-slate-200/70 px-2.5 py-1 text-[10px] font-medium text-slate-500">
              Task created • {formatRemarkDateHeader(Number(task.createdAt || Date.now()))}
            </span>
          </div>

          <div
            ref={remarksScrollRef}
            onScroll={handleMessagesScroll}
            className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain pb-2 pr-1"
          >
            {loadingMessages ? (
              <p className="py-5 text-center text-sm text-slate-400">Loading conversation...</p>
            ) : messages.length > 0 ? (
              <>
              {hasOlderMessages && (
                <div className="flex justify-center py-2">
                  <button
                    type="button"
                    onClick={loadOlderTaskMessages}
                    disabled={loadingOlderMessages}
                    className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-60"
                  >
                    {loadingOlderMessages ? 'Loading...' : 'Load older messages'}
                  </button>
                </div>
              )}
              {messages.map((message, index) => {
                const isOwn = isCurrentUserSenderId(message.sender_id);
                const showDateSeparator =
                  index === 0 ||
                  getChatDateLabel(message.created_at_ms) !== getChatDateLabel(messages[index - 1].created_at_ms);
                const previousMessage = index > 0 ? messages[index - 1] : null;
                const isGroupedWithPrevious = Boolean(
                  previousMessage &&
                  previousMessage.sender_id === message.sender_id &&
                  getChatDateLabel(previousMessage.created_at_ms) === getChatDateLabel(message.created_at_ms) &&
                  message.created_at_ms - previousMessage.created_at_ms < 5 * 60 * 1000
                );
                const nextMessage = index < messages.length - 1 ? messages[index + 1] : null;
                const isGroupedWithNext = Boolean(
                  nextMessage &&
                  nextMessage.sender_id === message.sender_id &&
                  getChatDateLabel(nextMessage.created_at_ms) === getChatDateLabel(message.created_at_ms) &&
                  nextMessage.created_at_ms - message.created_at_ms < 5 * 60 * 1000
                );
                const receiptStatus = getMessageReceiptStatus(message);
                const messageReactions = reactionsByMessageId[message.id] || [];
                const iReacted = messageReactions.some(
                  (reaction) => reaction.user_id === currentEmployeeId && reaction.emoji === '👍'
                );
                const canEditMessage = isOwn && isMessageWithinEditWindow(message.created_at);
                return (
                  <React.Fragment key={message.id}>
                    {showDateSeparator && (
                      <div className="flex justify-center py-1.5">
                        <span className="rounded-full bg-slate-200/70 px-2.5 py-0.5 text-[10px] font-semibold text-slate-500">
                          {getChatDateLabel(message.created_at_ms)}
                        </span>
                      </div>
                    )}
	                    <motion.div
                        initial={{ opacity: message.optimistic ? 0.7 : 0, y: message.optimistic ? 2 : 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.16 }}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'} ${isGroupedWithPrevious ? 'mt-0.5' : 'mt-2'}`}
                      >
                      <div
                        ref={(node) => {
                          messageRefs.current[message.id] = node;
                        }}
	                        className={`flex max-w-[96%] min-w-0 items-end gap-1.5 sm:max-w-[78%] ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
                      >
	                        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
	                          isOwn ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'
	                        } ${isGroupedWithPrevious ? 'invisible' : ''}`}>
	                          {message.sender_initials}
	                        </span>
		                        <div className={`min-w-0 ${isOwn ? 'items-end' : 'items-start'}`}>
                          {!isOwn && !isGroupedWithPrevious && (
                            <div className="mb-1 flex items-center gap-2">
                              <span className="text-xs font-bold text-slate-700">{message.sender_name}</span>
                            </div>
                          )}
	                          <div className={`group flex min-w-0 items-start gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}>
	                            <div className={`min-w-0 flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
                            <div
                              onContextMenu={(event) => openMessageActionMenu(event, message)}
                              onTouchStart={(event) => handleMessageTouchStart(event, message)}
                              onTouchMove={(event) => handleMessageTouchMove(event, message)}
                              onTouchEnd={handleMessageTouchEnd}
                              onTouchCancel={handleMessageTouchEnd}
	                              className={`px-3 py-2 shadow-sm transition-colors ${
	                                highlightedMessageId === message.id ? 'ring-2 ring-amber-300 ring-offset-2' : ''
	                              } ${
	                                isOwn
	                                  ? `${isGroupedWithPrevious ? 'rounded-tr-lg' : 'rounded-tr-[20px]'} ${isGroupedWithNext ? 'rounded-br-lg' : 'rounded-br-md'} rounded-l-[20px] bg-[#DFF7EA] text-slate-900`
	                                  : `${isGroupedWithPrevious ? 'rounded-tl-lg' : 'rounded-tl-[20px]'} ${isGroupedWithNext ? 'rounded-bl-lg' : 'rounded-bl-md'} rounded-r-[20px] border border-slate-200/80 bg-white text-slate-800`
	                              }`}
	                            >
                            {message.reply_to && (
	                              <button
	                                type="button"
	                                onClick={() => scrollToMessage(message.reply_to?.id || '')}
	                                className={`mb-2 block w-full max-w-full overflow-hidden rounded-2xl border px-3 py-2 text-left ${
	                                  isOwn
	                                    ? 'border-white/25 bg-white/15 text-white/90'
	                                    : 'border-slate-200 bg-slate-50 text-slate-700'
	                                }`}
	                              >
	                                <span className="text-sm">{'\u{1F44D}'}</span>
	                                <p className="max-w-full truncate text-[11px] font-bold">{message.reply_to.sender_name}</p>
	                                <p className="mt-1 line-clamp-2 max-w-full overflow-hidden break-words text-xs">
	                                  {getQuotedMessageText(message.reply_to)}
	                                </p>
	                              </button>
                            )}
                            {renderChatMediaPreview(message)}
                            {message.message_text.trim() && (
	                              <p className={`break-words text-[13px] leading-relaxed ${message.media_url ? 'mt-2' : ''}`}>
	                                {renderRemarkContent(message.message_text)}
	                              </p>
	                            )}
                            {message.optimistic && typeof message.upload_progress === 'number' && (
                              <div className="mt-2">
                                <div className={`h-1.5 overflow-hidden rounded-full ${isOwn ? 'bg-white/30' : 'bg-slate-200'}`}>
                                  <div
                                    className={`h-full rounded-full ${isOwn ? 'bg-white' : 'bg-emerald-500'}`}
                                    style={{ width: `${Math.max(8, Math.min(100, message.upload_progress))}%` }}
                                  />
                                </div>
                                <p className={`mt-1 text-[10px] ${isOwn ? 'text-white/80' : 'text-slate-500'}`}>
                                  Uploading…
                                </p>
                              </div>
                            )}
	                            {!isGroupedWithNext && (
	                            <div className={`mt-1.5 flex items-center gap-1 text-[10px] ${isOwn ? 'justify-end text-slate-500' : 'justify-end text-slate-400'}`}>
	                              {message.is_edited && <span>(edited)</span>}
	                              <span>{formatRemarkTime(message.created_at_ms)}</span>
	                              {isOwn && (
	                                receiptStatus === 'sent' ? (
	                                  <Check className="h-3.5 w-3.5 text-slate-400" />
	                                ) : (
	                                  <CheckCheck className={`h-3.5 w-3.5 ${receiptStatus === 'read' ? 'text-sky-500' : 'text-slate-400'}`} />
	                                )
	                              )}
	                            </div>
	                            )}
	                            </div>
	                            {messageReactions.length > 0 && (
	                              <MessageReactionChip
	                                messageId={message.id}
	                                count={messageReactions.length}
                                reactedByMe={iReacted}
                                names={getReactionDisplayNames(message.id)}
                                onToggle={toggleReaction}
	                                onRequestNames={fetchReactionNames}
	                              />
	                            )}
			                          </div>
	                            <div
	                              className={`flex shrink-0 items-center gap-1 pt-1 ${
	                                canEditMessage ? 'w-[124px]' : 'w-[84px]'
	                              } ${
	                                isOwn ? 'justify-end' : 'justify-start'
	                              } ${
	                                showAttachmentMenu
	                                  ? 'invisible pointer-events-none'
	                                  : 'opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100'
	                              }`}
	                            >
	                              <button
	                                type="button"
	                                onClick={(event) => {
	                                  event.stopPropagation();
	                                  void toggleReaction(message.id);
	                                }}
	                                onTouchEnd={(event) => handleReactionToggleTouchEnd(event, message.id)}
	                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full border shadow-sm transition-colors ${
	                                  iReacted
	                                    ? 'border-amber-200 bg-amber-50 text-amber-600'
	                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
	                                }`}
	                                aria-label={iReacted ? 'Remove thumbs up' : 'React with thumbs up'}
	                                title={iReacted ? 'Remove thumbs up' : 'React with thumbs up'}
	                              >
	                                <ThumbsUp className={`h-4 w-4 ${iReacted ? 'fill-current' : ''}`} />
	                              </button>
	                              {canEditMessage && (
	                                <button
	                                  type="button"
	                                  onClick={(event) => {
	                                    event.preventDefault();
	                                    event.stopPropagation();
	                                    beginEditingMessage(message);
	                                  }}
	                                  onTouchEnd={(event) => {
	                                    event.preventDefault();
	                                    event.stopPropagation();
	                                    beginEditingMessage(message);
	                                  }}
	                                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
	                                  aria-label="Edit message"
	                                  title="Edit message"
	                                >
	                                  <Edit className="h-4 w-4" />
	                                </button>
	                              )}
	                              <button
	                                type="button"
	                                onClick={(event) => {
	                                  event.preventDefault();
	                                  event.stopPropagation();
	                                  setReplyingToMessage(message);
	                                  setEditingMessage(null);
	                                  setMessageActionMenu(null);
	                                  requestAnimationFrame(() => remarkInputRef.current?.focus());
	                                }}
	                                onTouchEnd={(event) => {
	                                  event.preventDefault();
	                                  event.stopPropagation();
	                                  setReplyingToMessage(message);
	                                  setEditingMessage(null);
	                                  setMessageActionMenu(null);
	                                  requestAnimationFrame(() => remarkInputRef.current?.focus());
	                                }}
	                                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50"
	                                aria-label="Reply to message"
	                                title="Reply"
	                              >
	                                <Reply className="h-4 w-4" />
	                              </button>
		                            </div>
			                        </div>
		                      </div>
		                    </div>
		                    </motion.div>
                  </React.Fragment>
                );
              })}
              </>
            ) : (
              <p className="py-5 text-center text-sm text-slate-400">No discussion yet. Start the conversation below.</p>
            )}
            {!loadingMessages && messages.length > 0 && (
              <div className="mt-2 flex items-center gap-2 pl-9 text-[11px] font-medium text-slate-400">
                <span className="flex -space-x-1">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" />
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:120ms]" />
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:240ms]" />
                </span>
                Live room
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {showScrollBtn && (
            <button
              type="button"
              onClick={() => {
                setShowScrollBtn(false);
                scrollTaskMessagesToBottom('smooth');
              }}
              className="absolute bottom-4 right-4 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
              aria-label="Scroll to latest messages"
            >
              <ChevronDown className="h-4 w-4 stroke-[2.5]" />
            </button>
          )}
        </div>
          </motion.div>
        )}
        </AnimatePresence>
      </div>

      {/* ─── Chat Input Bar (sticky bottom) ─── */}
          {reactionTooltip && typeof document !== 'undefined' && createPortal(
            <div
              className="pointer-events-none fixed z-[220] hidden max-w-[220px] -translate-x-1/2 -translate-y-full whitespace-normal break-words rounded-lg bg-slate-900 px-3 py-2 text-center text-[11px] font-semibold leading-snug text-white shadow-lg sm:block"
              style={{ left: reactionTooltip.left, top: reactionTooltip.top }}
              role="tooltip"
            >
              {reactionTooltipNames.join(', ') || 'Unknown User'}
              <span className="absolute left-1/2 top-full h-2 w-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-slate-900" />
            </div>,
            document.body
          )}

		      {reactionViewerMessageId && (
	        <div className="fixed inset-0 z-[130] flex items-end bg-black/40 sm:hidden" onClick={() => setReactionViewerMessageId(null)}>
	          <div
		            className="max-h-[50vh] w-full overflow-y-auto rounded-t-3xl bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+80px)] pt-3 shadow-2xl"
	            onClick={(event) => event.stopPropagation()}
	          >
		            <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-300" />
		            <div className="mb-4 flex items-center justify-between gap-3">
		              <div>
		                <h2 className="text-base font-bold text-slate-900">Reacted by ({reactionViewerNames.length})</h2>
		              </div>
	              <button
	                type="button"
	                onClick={() => setReactionViewerMessageId(null)}
	                className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full bg-slate-100 text-slate-600"
	                aria-label="Close reactions"
	              >
	                <X className="h-5 w-5" />
	              </button>
	            </div>
	            <div className="space-y-2">
	              {reactionViewerNames.length > 0 ? (
	                reactionViewerNames.map((name, index) => (
		                  <div key={`${name}-${index}`} className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
		                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-700">
		                      {getEmployeeInitials(name)}
		                    </span>
		                    <span className="min-w-0 flex-1 break-words text-sm font-semibold text-slate-800">{name}</span>
		                  </div>
	                ))
	              ) : (
		                <div className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
		                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-700">
		                    U
		                  </span>
		                  <span className="text-sm font-semibold text-slate-500">Unknown User</span>
		                </div>
	              )}
	            </div>
	          </div>
	        </div>
	          )}

          {showAttachmentMenu && !editingMessage && attachmentMenuPosition && typeof document !== 'undefined' && createPortal(
            <div
              className="fixed z-[240] max-w-[calc(100vw-32px)] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
              style={{
                left: attachmentMenuPosition.left,
                bottom: attachmentMenuPosition.bottom,
                width: attachmentMenuPosition.width,
              }}
              onClick={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
            >
              {[
                { label: 'Camera', icon: <Camera className="h-4 w-4" />, onClick: () => cameraInputRef.current?.click() },
                { label: 'Photo Library', icon: <ImageIcon className="h-4 w-4" />, onClick: () => photoLibraryInputRef.current?.click() },
                { label: 'Document', icon: <Paperclip className="h-4 w-4" />, onClick: () => documentInputRef.current?.click() },
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={item.onClick}
                  className="flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>,
            document.body
          )}

	      {!readOnly && activeTab === 'chat' && (
      <div className="shrink-0 border-t border-slate-200/70 bg-white/90 px-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)] pt-2 shadow-[0_-10px_28px_rgba(15,23,42,0.07)] backdrop-blur md:px-6 md:pb-2">
        <div className="relative">
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*,video/*"
            capture="environment"
            className="hidden"
            onChange={handleChatMediaSelection}
          />
          <input
            ref={photoLibraryInputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={handleChatMediaSelection}
          />
          <input
            ref={documentInputRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip,audio/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={handleChatMediaSelection}
          />
          {messageActionMenu && (
            <div
              className="fixed z-[120] min-w-[150px] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
              style={{
                left: Math.min(messageActionMenu.x, window.innerWidth - 180),
                top: Math.min(messageActionMenu.y, window.innerHeight - 180),
              }}
              onClick={(event) => event.stopPropagation()}
            >
	              <button
	                type="button"
	                onClick={() => {
	                  void toggleReaction(messageActionMenu.messageId);
	                  setMessageActionMenu(null);
	                }}
	                onTouchEnd={(event) => {
	                  event.preventDefault();
	                  event.stopPropagation();
	                  void toggleReaction(messageActionMenu.messageId);
	                  setMessageActionMenu(null);
	                }}
	                className="flex min-h-[44px] w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
	              >
                <span className="text-base leading-none">👍</span>
                Like
              </button>
              <button
                type="button"
                onClick={() => {
                  const targetMessage = messages.find((message) => message.id === messageActionMenu.messageId);
                  if (targetMessage) {
                    setReplyingToMessage(targetMessage);
                    setEditingMessage(null);
                  }
                  setMessageActionMenu(null);
                }}
                className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                <Reply className="h-4 w-4" />
                Reply
              </button>
              {messageActionMenu.isOwn && messageActionMenu.canEdit && (
                <button
                  type="button"
	                  onClick={() => {
	                    const targetMessage = messages.find((message) => message.id === messageActionMenu.messageId);
	                    if (targetMessage) {
	                      beginEditingMessage(targetMessage);
	                    }
	                  }}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Edit className="h-4 w-4" />
                  Edit
                </button>
              )}
            </div>
          )}
          {mentionMenuOpen && (
            <div className="absolute left-0 right-0 bottom-[calc(100%+8px)] z-20 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
              {mentionLoading ? (
                <div className="px-3 py-2 text-xs text-slate-500">Loading team members…</div>
              ) : mentionCandidates.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500">No team members found</div>
              ) : (
                <ul className="max-h-56 overflow-y-auto py-1">
                  {mentionCandidates.map((candidate, index) => (
                    <li key={candidate.id}>
                      <button
                        type="button"
                        onClick={() => applyMentionCandidate(candidate)}
                        className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                          index === activeMentionIndex
                            ? 'bg-[var(--accent-light)] text-[var(--accent)]'
                            : 'text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        <span className="font-semibold">{candidate.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
	          )}

          {(replyingToMessage || editingMessage || selectedChatMedia) && (
            <div className="mb-2 space-y-2">
              {editingMessage && (
                <div className="flex items-start justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-700">Editing message</p>
                    <p className="mt-1 text-sm text-amber-900 line-clamp-2">{getQuotedMessageText(editingMessage)}</p>
                  </div>
                  <button type="button" onClick={() => { setEditingMessage(null); setNewRemark(''); }} className="rounded-full p-1 text-amber-700 hover:bg-amber-100">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
	              {replyingToMessage && !editingMessage && (
	                <div className="flex max-w-full items-start justify-between gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
	                  <div className="min-w-0 flex-1 overflow-hidden">
	                    <p className="max-w-full truncate text-[11px] font-bold uppercase tracking-[0.18em] text-slate-600">Replying to {replyingToMessage.sender_name}</p>
	                    <p className="mt-1 line-clamp-2 max-w-full overflow-hidden break-words text-sm text-slate-800">{getQuotedMessageText(replyingToMessage)}</p>
	                  </div>
	                  <button type="button" onClick={() => setReplyingToMessage(null)} className="rounded-full p-1 text-slate-600 hover:bg-slate-100">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
              {selectedChatMedia && (
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
                  <span className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-white">
                    {selectedChatMedia.mediaType === 'image' && selectedChatMedia.previewUrl ? (
                      <img src={selectedChatMedia.previewUrl} alt={selectedChatMedia.name} className="h-full w-full object-cover" />
                    ) : selectedChatMedia.mediaType === 'video' && selectedChatMedia.previewUrl ? (
                      <video src={selectedChatMedia.previewUrl} className="h-full w-full object-cover" />
                    ) : selectedChatMedia.mediaType === 'audio' ? (
                      <Music4 className="h-5 w-5 text-slate-600" />
                    ) : (
                      <FileText className="h-5 w-5 text-slate-600" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-slate-800">{selectedChatMedia.name}</p>
                    <p className="text-xs text-slate-500">{formatFileSize(selectedChatMedia.size)}</p>
                  </div>
                  <button type="button" onClick={clearSelectedChatMedia} className="rounded-full p-1 text-slate-600 hover:bg-slate-100">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* TaskComposer */}
	          <div className="mx-auto flex max-w-3xl items-end gap-1.5 rounded-[22px] border border-slate-200 bg-white p-1.5 shadow-[0_8px_24px_rgba(15,23,42,0.09)]">
	            <button
	              ref={attachmentButtonRef}
	              type="button"
	              onClick={(event) => {
	                event.stopPropagation();
	                if (!editingMessage) {
	                  setShowAttachmentMenu((prev) => {
	                    const next = !prev;
	                    if (next) {
	                      requestAnimationFrame(updateAttachmentMenuPosition);
	                    }
	                    return next;
	                  });
	                  setMessageActionMenu(null);
	                }
	              }}
              className={`rounded-2xl border border-slate-200 bg-slate-50 p-2.5 text-slate-700 transition-colors hover:bg-slate-100 ${editingMessage ? 'opacity-40' : ''}`}
              disabled={Boolean(editingMessage)}
              style={{ minHeight: 42, minWidth: 42 }}
              title="Attach media"
            >
              <Plus className="h-5 w-5" />
            </button>
            <textarea
              ref={remarkInputRef}
              value={newRemark}
              onChange={(e) => {
                const nextValue = e.target.value;
                setNewRemark(nextValue);
                const caret = e.target.selectionStart ?? nextValue.length;
                syncMentionMenuFromInput(nextValue, caret);
              }}
              placeholder={editingMessage ? 'Edit your message...' : 'Type a message... Use @ to mention'}
              className="max-h-24 min-h-[42px] flex-1 resize-none rounded-2xl border border-transparent bg-slate-50 px-3.5 py-2.5 text-[15px] text-slate-900 focus:border-[var(--accent)]/20 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/15"
              rows={1}
              onKeyDown={(e) => {
                if (mentionMenuOpen && mentionCandidates.length > 0) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActiveMentionIndex((prev) => (prev + 1) % mentionCandidates.length);
                    return;
                  }
                  if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActiveMentionIndex((prev) => (prev - 1 + mentionCandidates.length) % mentionCandidates.length);
                    return;
                  }
                  if (e.key === 'Enter' || e.key === 'Tab') {
                    e.preventDefault();
                    const selected = mentionCandidates[activeMentionIndex];
                    if (selected) {
                      applyMentionCandidate(selected);
                    }
                    return;
                  }
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    setMentionMenuOpen(false);
                    return;
                  }
                }

                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void handleAddRemark();
                }
              }}
            />
            <button
              type="button"
              onClick={() => void handleAddRemark()}
              disabled={!newRemark.trim() && !selectedChatMedia}
              className="rounded-2xl bg-[var(--accent)] p-2.5 text-white shadow-[0_8px_18px_rgba(79,70,229,0.24)] transition-all hover:bg-[#4338CA] active:scale-95 disabled:opacity-40"
              style={{ minHeight: 42, minWidth: 42 }}
              title={editingMessage ? 'Save message' : 'Send message'}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
      )}

      {/* ─── Edit Task Modal ─── */}
      {isEditing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={mobileEditOverlayStyle}
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !isSavingEdit && setIsEditing(false)} />
          <div
            className="relative bg-white w-full max-w-lg sm:max-w-xl max-h-[90vh] rounded-2xl shadow-xl overflow-hidden flex flex-col animate-in fade-in zoom-in-95"
            style={mobileEditModalStyle}
          >
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 sm:p-5 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Edit className="w-5 h-5 text-[var(--accent)]" />
                Edit Task
              </h2>
              {!isSavingEdit && (
                <button onClick={() => setIsEditing(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-500">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
            <div
              ref={editModalScrollRef}
              className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-4"
              style={mobileEditModalBodyStyle}
            >
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Task description"
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 resize-none min-h-[80px]"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="relative">
                  <select value={editTaskType} onChange={(e) => { setEditTaskType(e.target.value as TaskType); if (e.target.value === 'one_time') { setEditRecurrenceFrequency(''); setEditRecurrenceTime(getRoundedFiveMinuteTime()); } }}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 appearance-none pr-10">
                    <option value="one_time">One-time Task</option>
                    <option value="recurring">Recurring Task</option>
                  </select>
                  <Clock className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {editTaskType === 'recurring' && (
                  <div className="relative">
                    <select value={editRecurrenceFrequency} onChange={(e) => setEditRecurrenceFrequency(e.target.value === '' ? '' : (e.target.value as RecurrenceFrequency))}
                      className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 appearance-none pr-10">
                      <option value="">Select Frequency</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                )}
                {editTaskType === 'recurring' && editRecurrenceFrequency && (
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1 block">
                      Resurface time
                    </label>
                    <TwelveHourTimePicker
                      value={editRecurrenceTime}
                      onChange={setEditRecurrenceTime}
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="relative">
                  <select value={editAssigneeId} onChange={(e) => setEditAssigneeId(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 appearance-none pr-10">
                    <option value="none">Anyone / Unassigned</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>{emp.name} ({emp.role === 'super_admin' || emp.role === 'owner' ? 'Owner' : emp.role === 'manager' ? 'Manager' : 'Staff'})</option>
                    ))}
                  </select>
                  <UserPlus className="w-4 h-4 text-slate-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <div className="flex items-center gap-2">
                  <input type="datetime-local" value={editDeadline} onChange={(e) => setEditDeadline(e.target.value)} onInput={(e) => setEditDeadline(e.currentTarget.value)} onBlur={(e) => setEditDeadline(e.currentTarget.value)} onFocus={(e) => syncInputOnPickerClose(e.currentTarget, setEditDeadline)}
                    className="flex-1 border border-slate-200 rounded-xl px-4 py-3 bg-white text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20" />
                  {editDeadline && (
                    <button
                      type="button"
                      onClick={() => setEditDeadline('')}
                      className="text-slate-400 hover:text-slate-600 text-xs px-2 py-1 rounded-lg border border-slate-200 bg-white whitespace-nowrap"
                      aria-label="Clear date"
                    >
                      X Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="editHighPriority"
                  checked={editIsHighPriority}
                  onChange={(e) => setEditIsHighPriority(e.target.checked)}
                  className="w-5 h-5 text-red-500 rounded focus:ring-red-300 border-slate-300"
                />
                <label htmlFor="editHighPriority" className="text-sm text-slate-700">
                  Mark as High Priority
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={editRequirePhoto} onChange={(e) => setEditRequirePhoto(e.target.checked)}
                  className="w-5 h-5 text-[var(--accent)] rounded focus:ring-[var(--accent)]/20 border-slate-300" />
                Require photo proof
              </label>
            </div>
            <div className="shrink-0 border-t border-slate-200 bg-white p-4 sm:p-6">
              <div className="flex gap-3">
                <button type="button" onClick={() => setIsEditing(false)} disabled={isSavingEdit}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-xl font-semibold transition-all disabled:opacity-50" style={{ minHeight: 48 }}>
                  Cancel
                </button>
                <LoadingButton
                  type="button"
                  onClick={handleEditSave}
                  isLoading={isSavingEdit}
                  loadingText="Saving..."
                  variant="primary"
                  disabled={isSavingEdit}
                  className="flex-1 bg-[var(--accent)] hover:bg-[#4338CA] text-white py-3 rounded-xl font-bold transition-all disabled:opacity-60 active:scale-95"
                  style={{ minHeight: 48 }}
                >
                  Save Changes
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Proof Image Modal ─── */}
      {selectedProofPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Task Proof</h3>
              <button onClick={() => setSelectedProofIndex(null)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <div className="p-4">
              <div className="max-h-[65vh] overflow-y-auto rounded-lg">
                <img src={selectedProofPhoto.imageUrl} alt="Task proof" className="w-full h-auto object-contain rounded-lg" />
              </div>
              <div className="mt-3 space-y-2">
                <p className="text-sm font-medium text-slate-700">
                  Photo {(selectedProofIndex || 0) + 1} of {proofPhotos.length}
                </p>
                <p className="text-sm text-slate-500">Completed: {new Date(selectedProofPhoto.timestamp).toLocaleString()}</p>
              </div>
              {proofPhotos.length > 1 && (
                <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                  {proofPhotos.map((photo, index) => (
                    <button
                      key={`${photo.imageUrl}-${photo.timestamp}-thumb-${index}`}
                      type="button"
                      onClick={() => setSelectedProofIndex(index)}
                      className={`overflow-hidden rounded-xl border ${index === selectedProofIndex ? 'border-[var(--accent)]' : 'border-slate-200'}`}
                    >
                      <img
                        src={photo.imageUrl}
                        alt={`Task proof thumbnail ${index + 1}`}
                        className="h-16 w-16 object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {mediaPreview && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-950/90 p-4">
          <div className="relative w-full max-w-3xl rounded-3xl bg-slate-950 p-3">
            <button
              type="button"
              onClick={() => setMediaPreview(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-white/10 p-2 text-white backdrop-blur hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
            {mediaPreview.type === 'image' ? (
              <img src={mediaPreview.url} alt={mediaPreview.name} className="max-h-[82vh] w-full rounded-2xl object-contain" />
            ) : mediaPreview.type === 'video' ? (
              <video src={mediaPreview.url} controls autoPlay className="max-h-[82vh] w-full rounded-2xl" />
            ) : null}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TaskDetailsScreen;







